import '../styles/formsWorkspace.css';
import ModuleRecordSettings from '../components/ModuleRecordSettings';
import SignatureSettings from '../features/signatures/SignatureSettings';
import IntegratedFileCategorySelect from '../components/IntegratedFileCategorySelect';
import { isIntegratedAcademicField, integratedAcademicKind, integratedAcademicModule } from '../components/IntegratedAcademicSelect';
import ModuleIntegrationUpdateChoice from '../components/ModuleIntegrationUpdateChoice';
import { hydrateIntegratedFamilyFields, updateModuleIntegration } from '../utils/moduleIntegrationUpdate';
import { familyMemberIntegrationColumns } from '../utils/familyIntegrationFields';
import FormAiDocumentAttachment from '../components/FormAiDocumentAttachment';
import useFormAiDocument from '../hooks/useFormAiDocument';
import { buildFormAiRequest, normalizeFormAiSuggestion } from '../utils/formAiDocument';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FaTrash,
  FaPlus,
  FaArrowLeft,
  FaArrowRight,
  FaArrowUp,
  FaArrowDown,
  FaFont,
  FaAlignLeft,
  FaHashtag,
  FaListUl,
  FaFileUpload,
  FaCalculator,
  FaMagic,
  FaCog,
  FaGlobe,
  FaLink,
  FaCopy,
  FaPaste,
  FaPhone,
  FaEnvelope,
  FaIdCard,
  FaBuilding,
  FaCalendarAlt,
  FaCheckSquare,
  FaClock,
  FaMapMarkerAlt,
  FaUndo,
  FaRobot,
  FaGripVertical,
  FaTable,
  FaPlug,
  FaFilePdf,
  FaBold,
  FaUnderline,
  FaPalette,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaExpand,
  FaCompress,
  FaEye,
} from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../api';
import Modal from '../components/Modal';
import DecimalInput from "../components/DecimalInput";
import IntegerInput from "../components/IntegerInput";
import Toast from '../components/Toast';
import FormPreview from './FormPreview';
import MaskedInput from '../components/MaskedInput';
import {
  buildAttachmentFileName,
  collectTemplateFields,
  getSampleValueForField,
} from '../utils/attachmentNaming';
import { sanitizeDocumentTemplateHtml } from '../security/documentTemplateHtml';

const fieldChoiceOptions = field => (field?.academicOptions || field?.options || [])
  .map(option => typeof option === 'object' && option !== null
    ? { value: String(option.value ?? ''), label: String(option.label ?? '') }
    : { value: String(option ?? '').trim(), label: String(option ?? '').trim() })
  .filter(option => option.value);
const FIELD_TYPE_METADATA = {
  text: {
    label: 'Texto',
    description: 'Campo livre para qualquer informação.',
    icon: FaFont,
  },
  textarea: {
    label: 'Texto longo',
    description: 'Campo ampliado para respostas mais detalhadas.',
    icon: FaAlignLeft,
  },
  email: {
    label: 'E-mail',
    description: 'Valida endereços de e-mail automaticamente.',
    icon: FaEnvelope,
  },
  phone: {
    label: 'Telefone',
    description: 'Formata automaticamente números de telefone.',
    icon: FaPhone,
  },
  cpf: {
    label: 'CPF',
    description: 'Aplica máscara padrão de CPF.',
    icon: FaIdCard,
  },
  cnpj: {
    label: 'CNPJ',
    description: 'Aplica máscara padrão de CNPJ.',
    icon: FaBuilding,
  },
  cep: {
    label: 'CEP',
    description: 'Formata automaticamente o código postal brasileiro.',
    icon: FaMapMarkerAlt,
  },
  date: {
    label: 'Data',
    description: 'Campo com calendário para escolher a data.',
    icon: FaCalendarAlt,
  },
  time: {
    label: 'Hora',
    description: 'Permite selecionar um horário específico.',
    icon: FaClock,
  },
  number: {
    label: 'Número inteiro',
    description: 'Ideal para quantidades e identificadores numéricos.',
    icon: FaHashtag,
  },
  decimal: {
    label: 'Número decimal',
    description: 'Perfeito para valores monetários ou percentuais.',
    icon: FaCalculator,
  },
  currency: {
    label: 'Moeda (R$)',
    description: 'Aplica máscara monetária brasileira em reais.',
    icon: FaCalculator,
  },
  list: {
    label: 'Lista de opções',
    description: 'Permite escolher um item ou várias opções pré-definidas.',
    icon: FaListUl,
  },
  'dynamic-select': {
    label: 'Seleção colaborativa',
    description: 'Permite cadastrar opções em uma etapa e selecionar em outra.',
    icon: FaCheckSquare,
  },
  file: {
    label: 'Anexo',
    description: 'Uploads de documentos, comprovantes ou imagens.',
    icon: FaFileUpload,
  },
  table: {
    label: 'Tabela',
    description: 'Tabela com colunas pré-definidas e múltiplas linhas.',
    icon: FaTable,
  },
  linkedField: {
    label: 'Campo vinculado',
    description: 'Busca registros de módulos ou respostas de outro processo e preenche campos vinculados.',
    icon: FaLink,
  },
  'signature-attachment': { label: 'Anexo para Assinatura Eletrônica', description: 'PDF com aceite expresso, OTP por e-mail e evidências.', icon: FaFileUpload },
  document: {
    label: 'Gerar Documento',
    description: 'Modelo de texto rico que gera PDF com cabeçalho, rodapé, paginação e variáveis do formulário.',
    icon: FaFilePdf,
  },
};

const FORM_TEMPLATES = [
  {
    id: 'basic-registration',
    name: 'Cadastro de pessoa',
    description: 'Modelo com dados pessoais, contatos e endereço.',
    groups: [
      { id: 'dados', label: 'Dados pessoais' },
      { id: 'contato', label: 'Contato' },
      { id: 'endereco', label: 'Endereço' },
    ],
    fields: [
      { id: 'nomecompleto', label: 'Nome completo', type: 'text', size: 12, groupId: 'dados', required: true },
      { id: 'documento', label: 'Documento (CPF)', type: 'cpf', size: 6, groupId: 'dados', required: true },
      { id: 'estadocivil', label: 'Estado civil', type: 'list', size: 6, groupId: 'dados', options: ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)'] },
      { id: 'email', label: 'E-mail', type: 'email', size: 6, groupId: 'contato', required: true },
      { id: 'telefone', label: 'Telefone', type: 'phone', size: 6, groupId: 'contato' },
      { id: 'cep', label: 'CEP', type: 'cep', size: 4, groupId: 'endereco' },
      { id: 'logradouro', label: 'Logradouro', type: 'text', size: 8, groupId: 'endereco' },
      { id: 'cidade', label: 'Cidade', type: 'text', size: 6, groupId: 'endereco', required: true },
      { id: 'estado', label: 'Estado', type: 'list', size: 6, groupId: 'endereco', options: ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'] },
    ],
  },
  {
    id: 'service-request',
    name: 'Solicitação de serviço',
    description: 'Modelo para coleta de demandas internas ou externas.',
    groups: [
      { id: 'solicitante', label: 'Solicitante' },
      { id: 'detalhes', label: 'Detalhes da solicitação' },
    ],
    fields: [
      { id: 'nomesolicitante', label: 'Nome do solicitante', type: 'text', size: 12, groupId: 'solicitante', required: true },
      { id: 'emailsolicitante', label: 'E-mail do solicitante', type: 'email', size: 6, groupId: 'solicitante', required: true },
      { id: 'ramalsolicitante', label: 'Telefone/Ramal', type: 'phone', size: 6, groupId: 'solicitante' },
      { id: 'tiposervico', label: 'Tipo de serviço', type: 'list', size: 6, groupId: 'detalhes', required: true, options: ['Suporte técnico', 'Financeiro', 'Operacional', 'Outros'] },
      { id: 'prioridade', label: 'Prioridade', type: 'list', size: 6, groupId: 'detalhes', options: ['Baixa', 'Média', 'Alta'] },
      { id: 'descricao', label: 'Descrição da solicitação', type: 'text', size: 12, groupId: 'detalhes', required: true },
      { id: 'anexo', label: 'Anexo (opcional)', type: 'file', size: 12, groupId: 'detalhes' },
    ],
  },
  {
    id: 'reembolso',
    name: 'Pedido de reembolso',
    description: 'Modelo com informações financeiras e comprovantes.',
    groups: [
      { id: 'dadoscolaborador', label: 'Dados do colaborador' },
      { id: 'despesas', label: 'Detalhes da despesa' },
    ],
    fields: [
      { id: 'nomecolaborador', label: 'Nome', type: 'text', size: 12, groupId: 'dadoscolaborador', required: true },
      { id: 'centrodecusto', label: 'Centro de custo', type: 'text', size: 6, groupId: 'dadoscolaborador' },
      { id: 'departamento', label: 'Departamento', type: 'list', size: 6, groupId: 'dadoscolaborador', options: ['Administrativo', 'Financeiro', 'Operações', 'Tecnologia', 'Vendas'] },
      { id: 'tipodespesa', label: 'Tipo da despesa', type: 'list', size: 6, groupId: 'despesas', required: true, options: ['Transporte', 'Alimentação', 'Hospedagem', 'Outros'] },
      { id: 'valordespesa', label: 'Valor da despesa', type: 'decimal', size: 6, groupId: 'despesas', required: true, decimalPlaces: 2 },
      { id: 'datadespesa', label: 'Data da despesa', type: 'date', size: 6, groupId: 'despesas', required: true },
      { id: 'motivodespesa', label: 'Motivo', type: 'text', size: 12, groupId: 'despesas' },
      { id: 'comprovante', label: 'Comprovante', type: 'file', size: 12, groupId: 'despesas', required: true },
    ],
  },
];


const MODULE_INTEGRATION_TEMPLATES = [
  {
    id: 'module-student-management-student',
    name: 'Gestão de Alunos • Cadastro de aluno',
    targetModule: 'Gestão de Alunos',
    action: 'Cadastrar aluno',
    groups: [
      { id: 'dadospessoais', label: 'Dados pessoais' },
      { id: 'contato', label: 'Contato' },
      { id: 'endereco', label: 'Endereço' },
      { id: 'academico', label: 'Dados acadêmicos' },
      { id: 'responsavel', label: 'Responsável financeiro/pedagógico' },
    ],
    fields: [
      { id: 'nomealuno', label: 'Nome completo do aluno', type: 'text', size: 8, groupId: 'dadospessoais', required: true },
      { id: 'cpfaluno', label: 'CPF do aluno', type: 'cpf', size: 4, groupId: 'dadospessoais', required: true },
      { id: 'datanascimento', label: 'Data de nascimento', type: 'date', size: 4, groupId: 'dadospessoais', required: true },
      { id: 'emailaluno', label: 'E-mail do aluno', type: 'email', size: 4, groupId: 'contato', required: true },
      { id: 'telefonealuno', label: 'Telefone do aluno', type: 'phone', size: 4, groupId: 'contato' },
      { id: 'cepaluno', label: 'CEP', type: 'cep', size: 3, groupId: 'endereco' },
      { id: 'enderecoaluno', label: 'Endereço', type: 'text', size: 9, groupId: 'endereco' },
      { id: 'curso', label: 'Curso', type: 'text', size: 6, groupId: 'academico', required: true },
      { id: 'turma', label: 'Turma', type: 'text', size: 3, groupId: 'academico' },
      { id: 'statusmatricula', label: 'Status da matrícula', type: 'list', size: 3, groupId: 'academico', required: true, options: ['Pré-matrícula', 'Ativo', 'Trancado', 'Concluído'] },
      { id: 'nomeresponsavel', label: 'Nome do responsável', type: 'text', size: 6, groupId: 'responsavel' },
      { id: 'emailresponsavel', label: 'E-mail do responsável', type: 'email', size: 6, groupId: 'responsavel' },
    ],
  },
  {
    id: 'module-attendee-management-attendee',
    name: 'Gestão de Atendidos • Cadastro de atendido',
    targetModule: 'Gestão de Atendidos',
    action: 'Cadastrar atendido',
    groups: [
      { id: 'dadospessoais', label: 'Dados pessoais' },
      { id: 'contato', label: 'Contato' },
      { id: 'endereco', label: 'Endereço' },
      { id: 'academico', label: 'Dados acadêmicos' },
      { id: 'responsavel', label: 'Responsável financeiro/pedagógico' },
    ],
    fields: [
      { id: 'nomeatendido', label: 'Nome completo do atendido', type: 'text', size: 8, groupId: 'dadospessoais', required: true },
      { id: 'cpfatendido', label: 'CPF do atendido', type: 'cpf', size: 4, groupId: 'dadospessoais', required: true },
      { id: 'datanascimento', label: 'Data de nascimento', type: 'date', size: 4, groupId: 'dadospessoais', required: true },
      { id: 'emailatendido', label: 'E-mail do atendido', type: 'email', size: 4, groupId: 'contato', required: true },
      { id: 'telefoneatendido', label: 'Telefone do atendido', type: 'phone', size: 4, groupId: 'contato' },
      { id: 'cepatendido', label: 'CEP', type: 'cep', size: 3, groupId: 'endereco' },
      { id: 'enderecoatendido', label: 'Endereço', type: 'text', size: 9, groupId: 'endereco' },
      { id: 'grupo', label: 'Grupo', type: 'text', size: 6, groupId: 'academico', required: true },
      { id: 'turma', label: 'Turma', type: 'text', size: 3, groupId: 'academico' },
      { id: 'statusmatricula', label: 'Status da matrícula', type: 'list', size: 3, groupId: 'academico', required: true, options: ['Pré-matrícula', 'Ativo', 'Trancado', 'Concluído'] },
      { id: 'nomeresponsavel', label: 'Nome do responsável', type: 'text', size: 6, groupId: 'responsavel' },
      { id: 'emailresponsavel', label: 'E-mail do responsável', type: 'email', size: 6, groupId: 'responsavel' },
    ],
  },
  {
    id: 'module-inventory-item',
    name: 'Controle de Estoque • Cadastrar novo item',
    targetModule: 'Controle de Estoque',
    action: 'Cadastrar novo item',
    groups: [{ id: 'identificacaoitem', label: 'Identificação do item' }, { id: 'estoque', label: 'Parâmetros de estoque' }, { id: 'fornecimento', label: 'Fornecimento' }],
    fields: [
      { id: 'nomeitem', label: 'Nome do item', type: 'text', size: 6, groupId: 'identificacaoitem', required: true },
      { id: 'sku', label: 'SKU/Código interno', type: 'text', size: 3, groupId: 'identificacaoitem', required: true },
      { id: 'categoriaitem', label: 'Categoria', type: 'text', size: 3, groupId: 'identificacaoitem', required: true },
      { id: 'unidademedida', label: 'Unidade de medida', type: 'list', size: 4, groupId: 'estoque', required: true, options: ['Unidade', 'Caixa', 'Pacote', 'Kg', 'Litro', 'Metro'] },
      { id: 'estoqueminimo', label: 'Estoque mínimo', type: 'number', size: 4, groupId: 'estoque' },
      { id: 'estoqueinicial', label: 'Estoque inicial', type: 'number', size: 4, groupId: 'estoque' },
      { id: 'fornecedorpreferencial', label: 'Fornecedor preferencial', type: 'text', size: 6, groupId: 'fornecimento' },
      { id: 'custounitario', label: 'Custo unitário', type: 'decimal', size: 3, groupId: 'fornecimento', decimalPlaces: 2 },
    ],
  },
  {
    id: 'module-assets-asset',
    name: 'Patrimônio • Cadastrar novo ativo',
    targetModule: 'Patrimônio',
    action: 'Cadastrar novo ativo',
    groups: [{ id: 'ativo', label: 'Dados do ativo' }, { id: 'aquisicao', label: 'Aquisição' }, { id: 'alocacao', label: 'Alocação e responsabilidade' }],
    fields: [
      { id: 'nomeativo', label: 'Nome do ativo', type: 'text', size: 6, groupId: 'ativo', required: true },
      { id: 'tagpatrimonial', label: 'Etiqueta patrimonial', type: 'text', size: 3, groupId: 'ativo', required: true },
      { id: 'categoriaativo', label: 'Categoria do ativo', type: 'text', size: 3, groupId: 'ativo', required: true },
      { id: 'datacompra', label: 'Data de compra', type: 'date', size: 4, groupId: 'aquisicao' },
      { id: 'valorcompra', label: 'Valor de compra', type: 'decimal', size: 4, groupId: 'aquisicao', decimalPlaces: 2 },
      { id: 'notafiscal', label: 'Nota fiscal', type: 'file', size: 4, groupId: 'aquisicao' },
      { id: 'localizacao', label: 'Localização', type: 'text', size: 6, groupId: 'alocacao', required: true },
      { id: 'responsavelativo', label: 'Responsável pelo ativo', type: 'text', size: 6, groupId: 'alocacao' },
    ],
  },
  {
    id: 'module-hr-employee',
    name: 'Recursos Humanos • Cadastrar colaborador',
    targetModule: 'Recursos Humanos',
    action: 'Cadastrar colaborador',
    groups: [{ id: 'dadoscolaborador', label: 'Dados do colaborador' }, { id: 'contrato', label: 'Contrato' }, { id: 'documentos', label: 'Documentos' }],
    fields: [
      { id: 'nomecolaborador', label: 'Nome completo', type: 'text', size: 8, groupId: 'dadoscolaborador', required: true },
      { id: 'cpfcolaborador', label: 'CPF', type: 'cpf', size: 4, groupId: 'dadoscolaborador', required: true },
      { id: 'emailcorporativo', label: 'E-mail corporativo', type: 'email', size: 6, groupId: 'dadoscolaborador', required: true },
      { id: 'telefonecolaborador', label: 'Telefone', type: 'phone', size: 6, groupId: 'dadoscolaborador' },
      { id: 'cargo', label: 'Cargo', type: 'text', size: 4, groupId: 'contrato', required: true },
      { id: 'departamento', label: 'Departamento', type: 'text', size: 4, groupId: 'contrato', required: true },
      { id: 'dataadmissao', label: 'Data de admissão', type: 'date', size: 4, groupId: 'contrato', required: true },
      { id: 'documentospessoais', label: 'Documentos pessoais', type: 'file', size: 6, groupId: 'documentos' },
      { id: 'contratoassinado', label: 'Contrato assinado', type: 'file', size: 6, groupId: 'documentos' },
    ],
  },
  {
    id: 'module-hr-talent-bank',
    name: 'Recursos Humanos • Banco de Talentos',
    targetModule: 'Recursos Humanos',
    action: 'Cadastrar no Banco de Talentos',
    groups: [{ id: 'talentbankcandidate', label: 'Dados do candidato' }, { id: 'talentbankfiles', label: 'Currículo' }],
    fields: [
      { id: 'talent_candidateName', label: 'Nome', type: 'text', size: 6, groupId: 'talentbankcandidate', required: true },
      { id: 'talent_email', label: 'Email', type: 'email', size: 6, groupId: 'talentbankcandidate' },
      { id: 'talent_phone', label: 'Telefone', type: 'phone', size: 6, groupId: 'talentbankcandidate' },
      { id: 'talent_position', label: 'Vaga', type: 'list', size: 6, groupId: 'talentbankcandidate', options: [] },
      { id: 'talent_curriculum', label: 'Anexo do currículo', type: 'file', size: 12, groupId: 'talentbankfiles', required: true },
    ],
  },
  {
    id: 'module-project-bank-project',
    name: 'Banco de Projetos • Abrir novo projeto',
    targetModule: 'Banco de Projetos',
    action: 'Abrir novo projeto',
    groups: [{ id: 'projeto', label: 'Dados do projeto' }, { id: 'planejamento', label: 'Planejamento' }, { id: 'governanca', label: 'Governança' }],
    fields: [
      { id: 'nomeprojeto', label: 'Nome do projeto', type: 'text', size: 8, groupId: 'projeto', required: true },
      { id: 'patrocinador', label: 'Patrocinador', type: 'text', size: 4, groupId: 'projeto', required: true },
      { id: 'objetivo', label: 'Objetivo', type: 'textarea', size: 12, groupId: 'projeto', required: true },
      { id: 'datainicio', label: 'Data prevista de início', type: 'date', size: 4, groupId: 'planejamento' },
      { id: 'datafim', label: 'Data prevista de término', type: 'date', size: 4, groupId: 'planejamento' },
      { id: 'orcamento', label: 'Orçamento estimado', type: 'decimal', size: 4, groupId: 'planejamento', decimalPlaces: 2 },
      { id: 'gerenteprojeto', label: 'Gerente do projeto', type: 'text', size: 6, groupId: 'governanca' },
      { id: 'prioridadeprojeto', label: 'Prioridade', type: 'list', size: 6, groupId: 'governanca', options: ['Baixa', 'Média', 'Alta', 'Crítica'] },
    ],
  },
];


const MODULE_INTEGRATION_CONFIGS = {
  'module-student-management-student': {
    moduleKey: 'students',
    moduleLabel: 'Gestão de Alunos',
    uniqueFields: ['cpf'],
    fields: {
      nomealuno: 'fullName', cpfaluno: 'cpf', datanascimento: 'birthDate', emailaluno: 'email', telefonealuno: 'phone',
      curso: 'customFields.courseName', turma: 'customFields.className', statusmatricula: 'status',
      nomeresponsavel: 'family.responsibleName', emailresponsavel: 'family.responsibleEmail', cepaluno: 'address.zipCode', enderecoaluno: 'address.street',
    },
  },
  'module-attendee-management-attendee': {
    moduleKey: 'attendees',
    moduleLabel: 'Gestão de Atendidos',
    uniqueFields: ['cpf'],
    fields: {
      nomeatendido: 'fullName', cpfatendido: 'cpf', datanascimento: 'birthDate', emailatendido: 'email', telefoneatendido: 'phone',
      grupo: 'customFields.groupName', curso: 'customFields.groupName', turma: 'customFields.className', statusmatricula: 'status',
      nomeresponsavel: 'family.responsibleName', emailresponsavel: 'family.responsibleEmail', cepatendido: 'address.zipCode', enderecoatendido: 'address.street',
    },
  },
  'module-inventory-item': {
    moduleKey: 'inventory', moduleLabel: 'Controle de Estoque', uniqueFields: ['sku', 'name'],
    fields: { nomeitem: 'name', sku: 'sku', categoriaitem: 'categoryId', unidademedida: 'unit', estoqueminimo: 'minimumStock', estoqueinicial: 'initialQuantity', fornecedorpreferencial: 'supplierName', custounitario: 'costPrice' },
  },
  'module-assets-asset': {
    moduleKey: 'assets', moduleLabel: 'Patrimônio', uniqueFields: ['tag', 'serialNumber'],
    fields: { nomeativo: 'name', tagpatrimonial: 'tag', categoriaativo: 'categoryName', datacompra: 'acquisitionDate', valorcompra: 'acquisitionCost', notafiscal: 'attachments', localizacao: 'locationName', responsavelativo: 'custodian' },
  },
  'module-hr-employee': {
    moduleKey: 'employees', moduleLabel: 'Recursos Humanos', uniqueFields: ['cpf', 'registrationNumber'],
    fields: { nomecolaborador: 'fullName', cpfcolaborador: 'cpf', emailcorporativo: 'email', telefonecolaborador: 'phone', cargo: 'position', departamento: 'department', dataadmissao: 'hireDate', documentospessoais: 'files.personalDocuments', contratoassinado: 'files.signedContract' },
  },
  'module-hr-talent-bank': {
    moduleKey: 'talentBank', moduleLabel: 'Recursos Humanos', uniqueFields: [],
    fields: { talent_candidateName: 'candidateName', talent_email: 'email', talent_phone: 'phone', talent_position: 'position', talent_curriculum: 'files' },
  },
  'module-project-bank-project': {
    moduleKey: 'projects', moduleLabel: 'Banco de Projetos', uniqueFields: ['title'],
    fields: { nomeprojeto: 'title', patrocinador: 'mainResponsible', objetivo: 'generalObjective', datainicio: 'plannedStartDate', datafim: 'plannedEndDate', orcamento: 'customFields.estimatedBudget', gerenteprojeto: 'involvedTeam', prioridadeprojeto: 'priority' },
  },
};


const INVENTORY_ITEM_GROUPS = [
  { id: 'inventory_identification', label: 'Identificação do item' },
  { id: 'inventory_stock', label: 'Parâmetros de estoque' },
  { id: 'inventory_commercial', label: 'Comercial' },
  { id: 'inventory_tracking', label: 'Rastreabilidade' },
  { id: 'inventory_details', label: 'Detalhes' },
];

const INVENTORY_ITEM_FIELDS = [
  { id: 'inventory_name', label: 'Nome do produto', type: 'text', size: 6, groupId: 'inventory_identification', required: true, moduleField: 'name' },
  { id: 'inventory_sku', label: 'SKU / Código interno', type: 'text', size: 3, groupId: 'inventory_identification', moduleField: 'sku' },
  { id: 'inventory_categoryId', label: 'Categoria', type: 'list', size: 3, groupId: 'inventory_identification', options: [], moduleField: 'categoryId' },
  { id: 'inventory_warehouseId', label: 'Depósito padrão', type: 'list', size: 4, groupId: 'inventory_stock', options: [], moduleField: 'warehouseId' },
  { id: 'inventory_unit', label: 'Unidade de medida', type: 'text', size: 4, groupId: 'inventory_stock', moduleField: 'unit' },
  { id: 'inventory_initialQuantity', label: 'Estoque inicial', type: 'number', size: 4, groupId: 'inventory_stock', moduleField: 'initialQuantity' },
  { id: 'inventory_minimumStock', label: 'Estoque mínimo', type: 'number', size: 4, groupId: 'inventory_stock', moduleField: 'minimumStock' },
  { id: 'inventory_maximumStock', label: 'Estoque máximo', type: 'number', size: 4, groupId: 'inventory_stock', moduleField: 'maximumStock' },
  { id: 'inventory_safetyStock', label: 'Estoque de segurança', type: 'number', size: 4, groupId: 'inventory_stock', moduleField: 'safetyStock' },
  { id: 'inventory_costPrice', label: 'Custo unitário (R$)', type: 'decimal', size: 4, groupId: 'inventory_commercial', decimalPlaces: 2, moduleField: 'costPrice' },
  { id: 'inventory_salePrice', label: 'Preço de venda (R$)', type: 'decimal', size: 4, groupId: 'inventory_commercial', decimalPlaces: 2, moduleField: 'salePrice' },
  { id: 'inventory_trackBatch', label: 'Controlar lote', type: 'list', size: 4, groupId: 'inventory_tracking', options: ['Não', 'Sim'], moduleField: 'trackBatch' },
  { id: 'inventory_trackExpiration', label: 'Controlar validade', type: 'list', size: 4, groupId: 'inventory_tracking', options: ['Não', 'Sim'], moduleField: 'trackExpiration' },
  { id: 'inventory_expirationDate', label: 'Validade', type: 'date', size: 4, groupId: 'inventory_tracking', moduleField: 'expirationDate' },
  { id: 'inventory_active', label: 'Item ativo', type: 'list', size: 4, groupId: 'inventory_tracking', options: ['Sim', 'Não'], moduleField: 'active' },
  { id: 'inventory_description', label: 'Descrição', type: 'textarea', size: 12, groupId: 'inventory_details', moduleField: 'description' },
];

const STUDENT_MANAGEMENT_GROUPS = [
  { id: 'student_personal', label: 'Dados Pessoais' },
  { id: 'student_family', label: 'Vínculo Familiar' },
  { id: 'student_address', label: 'Endereço' },
  { id: 'student_social', label: 'Social' },
  { id: 'student_education', label: 'Escolaridade' },
  { id: 'student_health', label: 'Saúde' },
  { id: 'student_course', label: 'Curso' },
  { id: 'student_files', label: 'Arquivos' },
  { id: 'student_confidential', label: 'Confidencial' },
  { id: 'student_more', label: 'Mais informações' },
];

const STUDENT_MANAGEMENT_FIELDS = [
  { id: 'student_photoUrl', label: 'Foto (opcional)', type: 'file', size: 4, groupId: 'student_personal', required: false, moduleField: 'photoUrl' },
  { id: 'student_fullName', label: 'Nome completo', type: 'text', size: 4, groupId: 'student_personal', required: true, moduleField: 'fullName' },
  { id: 'student_socialName', label: 'Nome social', type: 'text', size: 3, groupId: 'student_personal', moduleField: 'socialName' },
  { id: 'student_enrollmentDate', label: 'Data de inscrição', type: 'date', size: 3, groupId: 'student_personal', moduleField: 'enrollmentDate', autoFillSource: 'today_date', autoFillEditable: false },
  { id: 'student_birthDate', label: 'Data de nascimento', type: 'date', size: 2, groupId: 'student_personal', moduleField: 'birthDate' },
  { id: 'student_gender', label: 'Gênero', type: 'list', size: 2, groupId: 'student_personal', options: ['Masculino', 'Feminino', 'Outro', 'Prefiro não dizer'], moduleField: 'gender' },
  { id: 'student_ethnicity', label: 'Etnia/cor', type: 'list', size: 2, groupId: 'student_personal', options: ['Preto', 'Pardo', 'Branco', 'Indígena', 'Amarelo'], moduleField: 'ethnicity' },
  { id: 'student_whatsapp', label: 'Whatsapp', type: 'phone', size: 2, groupId: 'student_personal', moduleField: 'whatsapp' },
  { id: 'student_phone', label: 'Telefone', type: 'phone', size: 2, groupId: 'student_personal', moduleField: 'phone' },
  { id: 'student_email', label: 'E-mail', type: 'email', size: 4, groupId: 'student_personal', moduleField: 'email' },
  { id: 'student_nationality', label: 'Nacionalidade', type: 'text', size: 3, groupId: 'student_personal', moduleField: 'nationality' },
  { id: 'student_nis', label: 'NIS', type: 'text', size: 3, groupId: 'student_personal', moduleField: 'nis' },
  { id: 'student_susCardNumber', label: 'Número do cartão do SUS', type: 'text', size: 4, groupId: 'student_personal', moduleField: 'susCardNumber' },
  { id: 'student_birthCertificateNumber', label: 'Número da certidão de nascimento', type: 'text', size: 6, groupId: 'student_personal', moduleField: 'birthCertificateNumber' },
  { id: 'student_cpf', label: 'CPF', type: 'cpf', size: 2, groupId: 'student_personal', required: true, moduleField: 'cpf' },
  { id: 'student_rg', label: 'RG', type: 'text', size: 2, groupId: 'student_personal', moduleField: 'rg' },
  { id: 'student_exitDate', label: 'Data de saída', type: 'date', size: 2, groupId: 'student_personal', moduleField: 'exitDate' },
  { id: 'student_exitReason', label: 'Motivo de saída', type: 'list', size: 4, groupId: 'student_personal', options: [], moduleField: 'exitReason' },
  { id: 'student_responsible1Name', label: 'Nome do Responsável 1', type: 'text', size: 4, groupId: 'student_family', moduleField: 'family.responsible1.name' },
  { id: 'student_responsible1Cpf', label: 'CPF do Responsável 1', type: 'text', mask: 'cpf', size: 4, groupId: 'student_family', moduleField: 'family.responsible1.cpf' },
  { id: 'student_responsible1Whatsapp', label: 'Whatsapp do Responsável 1', type: 'phone', size: 4, groupId: 'student_family', moduleField: 'family.responsible1.whatsapp' },
  { id: 'student_responsible1Profession', label: 'Profissão do Responsável 1', type: 'text', size: 4, groupId: 'student_family', moduleField: 'family.responsible1.profession' },
  { id: 'student_responsible1CompanyName', label: 'Nome da empresa do Responsável 1', type: 'text', size: 4, groupId: 'student_family', moduleField: 'family.responsible1.companyName' },
  { id: 'student_responsible1Workplace', label: 'Local de trabalho do Responsável 1', type: 'text', size: 4, groupId: 'student_family', moduleField: 'family.responsible1.workplace' },
  { id: 'student_responsible1LivesWithStudent', label: 'Responsável 1 reside com o aluno', type: 'list', size: 4, groupId: 'student_family', options: ['Não', 'Sim'], moduleField: 'family.responsible1.livesWithStudent' },
  { id: 'student_responsible2Name', label: 'Nome do Responsável 2', type: 'text', size: 4, groupId: 'student_family', moduleField: 'family.responsible2.name' },
  { id: 'student_responsible2Cpf', label: 'CPF do Responsável 2', type: 'text', mask: 'cpf', size: 4, groupId: 'student_family', moduleField: 'family.responsible2.cpf' },
  { id: 'student_responsible2Whatsapp', label: 'Whatsapp do Responsável 2', type: 'phone', size: 4, groupId: 'student_family', moduleField: 'family.responsible2.whatsapp' },
  { id: 'student_responsible2Profession', label: 'Profissão do Responsável 2', type: 'text', size: 4, groupId: 'student_family', moduleField: 'family.responsible2.profession' },
  { id: 'student_responsible2CompanyName', label: 'Nome da empresa do Responsável 2', type: 'text', size: 4, groupId: 'student_family', moduleField: 'family.responsible2.companyName' },
  { id: 'student_responsible2Workplace', label: 'Local de trabalho do Responsável 2', type: 'text', size: 4, groupId: 'student_family', moduleField: 'family.responsible2.workplace' },
  { id: 'student_responsible2LivesWithStudent', label: 'Responsável 2 reside com o aluno', type: 'list', size: 4, groupId: 'student_family', options: ['Não', 'Sim'], moduleField: 'family.responsible2.livesWithStudent' },
  { id: 'student_familyMembers', label: 'Composição familiar', type: 'table', size: 12, groupId: 'student_family', moduleField: 'family.members', tableColumns: familyMemberIntegrationColumns() },
  { id: 'student_zipCode', label: 'CEP', type: 'cep', size: 3, groupId: 'student_address', moduleField: 'address.zipCode' },
  { id: 'student_street', label: 'Rua', type: 'text', size: 5, groupId: 'student_address', moduleField: 'address.street' },
  { id: 'student_number', label: 'Número', type: 'text', size: 2, groupId: 'student_address', moduleField: 'address.number' },
  { id: 'student_complement', label: 'Complemento', type: 'text', size: 2, groupId: 'student_address', moduleField: 'address.complement' },
  { id: 'student_neighborhood', label: 'Bairro', type: 'text', size: 3, groupId: 'student_address', moduleField: 'address.neighborhood' },
  { id: 'student_city', label: 'Cidade', type: 'text', size: 3, groupId: 'student_address', moduleField: 'address.city' },
  { id: 'student_state', label: 'Estado', type: 'text', size: 2, groupId: 'student_address', moduleField: 'address.state' },
  { id: 'student_zone', label: 'Zona de residência', type: 'text', size: 2, groupId: 'student_address', moduleField: 'address.zone' },
  { id: 'student_housingType', label: 'Tipo de moradia', type: 'list', size: 2, groupId: 'student_address', options: ['Própria', 'Aluguel', 'Cedida', 'Financiada', 'Invasão/Ocupação', 'Situação de rua', 'Outro'], moduleField: 'address.housingType' },
  { id: 'student_cras', label: 'CRAS de referência', type: 'text', size: 6, groupId: 'student_social', moduleField: 'social.cras' },
  { id: 'student_familyIncome', label: 'Renda Familiar', type: 'currency', size: 6, groupId: 'student_social', moduleField: 'social.familyIncome' },
  { id: 'student_benefits', label: 'Benefícios do Governo', type: 'table', size: 12, groupId: 'student_social', moduleField: 'social.benefits', tableColumns: [{ id: 'name', label: 'Nome', type: 'text' }, { id: 'value', label: 'Valor', type: 'text' }] },
  { id: 'student_educationLevel', label: 'Escolaridade', type: 'list', size: 3, groupId: 'student_education', options: ['Ensino fundamental', 'Ensino médio', 'Superior', 'Pós', 'Mestrado', 'Doutorado'], moduleField: 'education.level' },
  { id: 'student_school', label: 'Escola', type: 'text', size: 3, groupId: 'student_education', moduleField: 'education.school' },
  { id: 'student_grade', label: 'Série/Ano', type: 'text', size: 3, groupId: 'student_education', moduleField: 'education.grade' },
  { id: 'student_period', label: 'Período em que estuda', type: 'text', size: 3, groupId: 'student_education', moduleField: 'education.period' },
  { id: 'student_extraCourses', label: 'Cursos extras', type: 'table', size: 12, groupId: 'student_education', moduleField: 'education.extraCourses', tableColumns: [{ id: 'name', label: 'Curso', type: 'text' }, { id: 'institution', label: 'Instituição', type: 'text' }] },
  ...[['allergy', 'Possui alergia?'], ['healthIssue', 'Possui algum problema particular de saúde?'], ['foodRestriction', 'Restrição alimentar?'], ['controlledMedication', 'Faz uso de medicação controlada?'], ['activitySupport', 'Necessita de acompanhamento durante as atividades?'], ['physicalLimitation', 'Limitação para atividades físicas?'], ['psychoactiveSubstances', 'Aluno faz ou fez uso de substâncias psicoativas?'], ['disability', 'Possui deficiência?']].flatMap(([key, label]) => [
    { id: `student_health_${key}`, label, type: 'list', size: 6, groupId: 'student_health', options: ['Não', 'Sim'], moduleField: `health.${key}.answer` },
    { id: `student_health_${key}_details`, label: `${label} Quais?`, type: 'text', size: 6, groupId: 'student_health', moduleField: `health.${key}.details`, visibleWhen: `student_health_${key} === 'Sim'` },
  ]),
  { id: 'student_courseId', label: 'Curso', type: 'list', size: 4, groupId: 'student_course', moduleField: 'education.courseId', options: [] },
  { id: 'student_classId', label: 'Turma vinculada', type: 'list', size: 4, groupId: 'student_course', moduleField: 'classId', options: [] },
  { id: 'student_classLinkedAt', label: 'Data de vínculo de turma', type: 'date', size: 4, groupId: 'student_course', moduleField: 'classLinkedAt' },
  { id: 'student_courseHistory', label: 'Histórico de cursos do aluno', type: 'table', size: 12, groupId: 'student_course', moduleField: 'education.courseHistory', tableColumns: [{ id: 'name', label: 'Curso/Turma', type: 'text' }, { id: 'startDate', label: 'Início', type: 'date' }, { id: 'endDate', label: 'Fim', type: 'date' }, { id: 'status', label: 'Status', type: 'text' }] },
  { id: 'student_documents', label: 'Documentos anexados', type: 'table', size: 12, groupId: 'student_files', moduleField: 'files.documents', tableColumns: [{ id: 'category', label: 'Categoria', type: 'list' }, { id: 'document', label: 'Documento anexado', type: 'file' }] },
  { id: 'student_confidentialNotes', label: 'Observações confidenciais', type: 'textarea', size: 12, groupId: 'student_confidential', moduleField: 'confidential.notes' },
  { id: 'student_customFields', label: 'Campos personalizados / Mais informações', type: 'table', size: 12, groupId: 'student_more', moduleField: 'customFields.processFields', tableColumns: [{ id: 'label', label: 'Campo', type: 'text' }, { id: 'value', label: 'Valor', type: 'text' }] },
];

const ATTENDEE_MANAGEMENT_GROUPS = [
  { id: 'attendee_personal', label: 'Dados Pessoais' },
  { id: 'attendee_family', label: 'Vínculo Familiar' },
  { id: 'attendee_address', label: 'Endereço' },
  { id: 'attendee_social', label: 'Social' },
  { id: 'attendee_education', label: 'Escolaridade' },
  { id: 'attendee_health', label: 'Saúde' },
  { id: 'attendee_group', label: 'Grupo' },
  { id: 'attendee_files', label: 'Arquivos' },
  { id: 'attendee_confidential', label: 'Confidencial' },
  { id: 'attendee_more', label: 'Mais informações' },
];

const ATTENDEE_MANAGEMENT_FIELDS = [
  { id: 'attendee_photoUrl', label: 'Foto (opcional)', type: 'file', size: 4, groupId: 'attendee_personal', required: false, moduleField: 'photoUrl' },
  { id: 'attendee_fullName', label: 'Nome completo', type: 'text', size: 4, groupId: 'attendee_personal', required: true, moduleField: 'fullName' },
  { id: 'attendee_socialName', label: 'Nome social', type: 'text', size: 3, groupId: 'attendee_personal', moduleField: 'socialName' },
  { id: 'attendee_enrollmentDate', label: 'Data de inscrição', type: 'date', size: 3, groupId: 'attendee_personal', moduleField: 'enrollmentDate', autoFillSource: 'today_date', autoFillEditable: false },
  { id: 'attendee_birthDate', label: 'Data de nascimento', type: 'date', size: 2, groupId: 'attendee_personal', moduleField: 'birthDate' },
  { id: 'attendee_gender', label: 'Gênero', type: 'list', size: 2, groupId: 'attendee_personal', options: ['Masculino', 'Feminino', 'Outro', 'Prefiro não dizer'], moduleField: 'gender' },
  { id: 'attendee_ethnicity', label: 'Etnia/cor', type: 'list', size: 2, groupId: 'attendee_personal', options: ['Preto', 'Pardo', 'Branco', 'Indígena', 'Amarelo'], moduleField: 'ethnicity' },
  { id: 'attendee_whatsapp', label: 'Whatsapp', type: 'phone', size: 2, groupId: 'attendee_personal', moduleField: 'whatsapp' },
  { id: 'attendee_phone', label: 'Telefone', type: 'phone', size: 2, groupId: 'attendee_personal', moduleField: 'phone' },
  { id: 'attendee_email', label: 'E-mail', type: 'email', size: 4, groupId: 'attendee_personal', moduleField: 'email' },
  { id: 'attendee_nationality', label: 'Nacionalidade', type: 'text', size: 3, groupId: 'attendee_personal', moduleField: 'nationality' },
  { id: 'attendee_nis', label: 'NIS', type: 'text', size: 3, groupId: 'attendee_personal', moduleField: 'nis' },
  { id: 'attendee_susCardNumber', label: 'Número do cartão do SUS', type: 'text', size: 4, groupId: 'attendee_personal', moduleField: 'susCardNumber' },
  { id: 'attendee_birthCertificateNumber', label: 'Número da certidão de nascimento', type: 'text', size: 6, groupId: 'attendee_personal', moduleField: 'birthCertificateNumber' },
  { id: 'attendee_cpf', label: 'CPF', type: 'cpf', size: 2, groupId: 'attendee_personal', required: true, moduleField: 'cpf' },
  { id: 'attendee_rg', label: 'RG', type: 'text', size: 2, groupId: 'attendee_personal', moduleField: 'rg' },
  { id: 'attendee_exitDate', label: 'Data de saída', type: 'date', size: 2, groupId: 'attendee_personal', moduleField: 'exitDate' },
  { id: 'attendee_exitReason', label: 'Motivo de saída', type: 'list', size: 4, groupId: 'attendee_personal', options: [], moduleField: 'exitReason' },
  { id: 'attendee_responsible1Name', label: 'Nome do Responsável 1', type: 'text', size: 4, groupId: 'attendee_family', moduleField: 'family.responsible1.name' },
  { id: 'attendee_responsible1Cpf', label: 'CPF do Responsável 1', type: 'text', mask: 'cpf', size: 2, groupId: 'attendee_family', moduleField: 'family.responsible1.cpf' },
  { id: 'attendee_responsible1Whatsapp', label: 'Whatsapp do Responsável 1', type: 'phone', size: 2, groupId: 'attendee_family', moduleField: 'family.responsible1.whatsapp' },
  { id: 'attendee_responsible1Profession', label: 'Profissão do Responsável 1', type: 'text', size: 4, groupId: 'attendee_family', moduleField: 'family.responsible1.profession' },
  { id: 'attendee_responsible1CompanyName', label: 'Nome da empresa do Responsável 1', type: 'text', size: 4, groupId: 'attendee_family', moduleField: 'family.responsible1.companyName' },
  { id: 'attendee_responsible1Workplace', label: 'Local de trabalho do Responsável 1', type: 'text', size: 4, groupId: 'attendee_family', moduleField: 'family.responsible1.workplace' },
  { id: 'attendee_responsible1LivesWithAttendee', label: 'Responsável 1 reside com o atendido', type: 'list', size: 4, groupId: 'attendee_family', options: ['Não', 'Sim'], moduleField: 'family.responsible1.livesWithAttendee' },
  { id: 'attendee_responsible2Name', label: 'Nome do Responsável 2', type: 'text', size: 4, groupId: 'attendee_family', moduleField: 'family.responsible2.name' },
  { id: 'attendee_responsible2Cpf', label: 'CPF do Responsável 2', type: 'text', mask: 'cpf', size: 2, groupId: 'attendee_family', moduleField: 'family.responsible2.cpf' },
  { id: 'attendee_responsible2Whatsapp', label: 'Whatsapp do Responsável 2', type: 'phone', size: 2, groupId: 'attendee_family', moduleField: 'family.responsible2.whatsapp' },
  { id: 'attendee_responsible2Profession', label: 'Profissão do Responsável 2', type: 'text', size: 4, groupId: 'attendee_family', moduleField: 'family.responsible2.profession' },
  { id: 'attendee_responsible2CompanyName', label: 'Nome da empresa do Responsável 2', type: 'text', size: 4, groupId: 'attendee_family', moduleField: 'family.responsible2.companyName' },
  { id: 'attendee_responsible2Workplace', label: 'Local de trabalho do Responsável 2', type: 'text', size: 4, groupId: 'attendee_family', moduleField: 'family.responsible2.workplace' },
  { id: 'attendee_responsible2LivesWithAttendee', label: 'Responsável 2 reside com o atendido', type: 'list', size: 4, groupId: 'attendee_family', options: ['Não', 'Sim'], moduleField: 'family.responsible2.livesWithAttendee' },
  { id: 'attendee_familyMembers', label: 'Composição familiar', type: 'table', size: 12, groupId: 'attendee_family', moduleField: 'family.members', tableColumns: familyMemberIntegrationColumns() },
  { id: 'attendee_zipCode', label: 'CEP', type: 'cep', size: 3, groupId: 'attendee_address', moduleField: 'address.zipCode' },
  { id: 'attendee_street', label: 'Rua', type: 'text', size: 5, groupId: 'attendee_address', moduleField: 'address.street' },
  { id: 'attendee_number', label: 'Número', type: 'text', size: 2, groupId: 'attendee_address', moduleField: 'address.number' },
  { id: 'attendee_complement', label: 'Complemento', type: 'text', size: 2, groupId: 'attendee_address', moduleField: 'address.complement' },
  { id: 'attendee_neighborhood', label: 'Bairro', type: 'text', size: 3, groupId: 'attendee_address', moduleField: 'address.neighborhood' },
  { id: 'attendee_city', label: 'Cidade', type: 'text', size: 3, groupId: 'attendee_address', moduleField: 'address.city' },
  { id: 'attendee_state', label: 'Estado', type: 'text', size: 2, groupId: 'attendee_address', moduleField: 'address.state' },
  { id: 'attendee_zone', label: 'Zona de residência', type: 'text', size: 2, groupId: 'attendee_address', moduleField: 'address.zone' },
  { id: 'attendee_housingType', label: 'Tipo de moradia', type: 'list', size: 2, groupId: 'attendee_address', options: ['Própria', 'Aluguel', 'Cedida', 'Financiada', 'Invasão/Ocupação', 'Situação de rua', 'Outro'], moduleField: 'address.housingType' },
  { id: 'attendee_cras', label: 'CRAS de referência', type: 'text', size: 6, groupId: 'attendee_social', moduleField: 'social.cras' },
  { id: 'attendee_familyIncome', label: 'Renda Familiar', type: 'currency', size: 6, groupId: 'attendee_social', moduleField: 'social.familyIncome' },
  { id: 'attendee_benefits', label: 'Benefícios do Governo', type: 'table', size: 12, groupId: 'attendee_social', moduleField: 'social.benefits', tableColumns: [{ id: 'name', label: 'Nome', type: 'text' }, { id: 'value', label: 'Valor', type: 'text' }] },
  { id: 'attendee_educationLevel', label: 'Escolaridade', type: 'list', size: 3, groupId: 'attendee_education', options: ['Ensino fundamental', 'Ensino médio', 'Superior', 'Pós', 'Mestrado', 'Doutorado'], moduleField: 'education.level' },
  { id: 'attendee_school', label: 'Escola', type: 'text', size: 3, groupId: 'attendee_education', moduleField: 'education.school' },
  { id: 'attendee_grade', label: 'Série/Ano', type: 'text', size: 3, groupId: 'attendee_education', moduleField: 'education.grade' },
  { id: 'attendee_period', label: 'Período em que estuda', type: 'text', size: 3, groupId: 'attendee_education', moduleField: 'education.period' },
  { id: 'attendee_extraCourses', label: 'Cursos extras', type: 'table', size: 12, groupId: 'attendee_education', moduleField: 'education.extraCourses', tableColumns: [{ id: 'name', label: 'Curso', type: 'text' }, { id: 'institution', label: 'Instituição', type: 'text' }] },
  ...[['allergy', 'Possui alergia?'], ['healthIssue', 'Possui algum problema particular de saúde?'], ['foodRestriction', 'Restrição alimentar?'], ['controlledMedication', 'Faz uso de medicação controlada?'], ['activitySupport', 'Necessita de acompanhamento durante as atividades?'], ['physicalLimitation', 'Limitação para atividades físicas?'], ['psychoactiveSubstances', 'Atendido faz ou fez uso de substâncias psicoativas?'], ['disability', 'Possui deficiência?']].flatMap(([key, label]) => [
    { id: `attendee_health_${key}`, label, type: 'list', size: 6, groupId: 'attendee_health', options: ['Não', 'Sim'], moduleField: `health.${key}.answer` },
    { id: `attendee_health_${key}_details`, label: `${label} Quais?`, type: 'text', size: 6, groupId: 'attendee_health', moduleField: `health.${key}.details`, visibleWhen: `attendee_health_${key} === 'Sim'` },
  ]),
  { id: 'attendee_groupId', label: 'Curso', type: 'list', size: 4, groupId: 'attendee_group', moduleField: 'education.groupId', options: [] },
  { id: 'attendee_classId', label: 'Turma vinculada', type: 'list', size: 4, groupId: 'attendee_group', moduleField: 'classId', options: [] },
  { id: 'attendee_classLinkedAt', label: 'Data de vínculo de turma', type: 'date', size: 4, groupId: 'attendee_group', moduleField: 'classLinkedAt' },
  { id: 'attendee_groupHistory', label: 'Histórico de grupos do atendido', type: 'table', size: 12, groupId: 'attendee_group', moduleField: 'education.groupHistory', tableColumns: [{ id: 'name', label: 'Grupo', type: 'text' }, { id: 'className', label: 'Turma', type: 'text' }, { id: 'startDate', label: 'Início', type: 'date' }, { id: 'endDate', label: 'Fim', type: 'date' }, { id: 'status', label: 'Status', type: 'text' }] },
  { id: 'attendee_documents', label: 'Documentos anexados', type: 'table', size: 12, groupId: 'attendee_files', moduleField: 'files.documents', tableColumns: [{ id: 'category', label: 'Categoria', type: 'list' }, { id: 'document', label: 'Documento anexado', type: 'file' }] },
  { id: 'attendee_confidentialNotes', label: 'Observações confidenciais', type: 'textarea', size: 12, groupId: 'attendee_confidential', moduleField: 'confidential.notes' },
  { id: 'attendee_customFields', label: 'Campos personalizados / Mais informações', type: 'table', size: 12, groupId: 'attendee_more', moduleField: 'customFields.processFields', tableColumns: [{ id: 'label', label: 'Campo', type: 'text' }, { id: 'value', label: 'Valor', type: 'text' }] },
];

const withModuleBindings = (template) => {
  const config = MODULE_INTEGRATION_CONFIGS[template.id];
  if (!config) return template;
  const sourceTemplate = template.id === 'module-student-management-student'
    ? { ...template, groups: STUDENT_MANAGEMENT_GROUPS, fields: STUDENT_MANAGEMENT_FIELDS }
    : template.id === 'module-attendee-management-attendee'
    ? { ...template, groups: ATTENDEE_MANAGEMENT_GROUPS, fields: ATTENDEE_MANAGEMENT_FIELDS }
    : template.id === 'module-inventory-item'
    ? { ...template, groups: INVENTORY_ITEM_GROUPS, fields: INVENTORY_ITEM_FIELDS }
    : template;
  const integrationApprovalGroup = { id: 'integration_status', label: 'Status da Integração' };
  const integrationApprovalField = {
    id: 'integration_approved',
    label: 'Aprovado?',
    type: 'list',
    size: 4,
    groupId: integrationApprovalGroup.id,
    required: true,
    options: ['Sim', 'Não'],
    integrationApprovalGate: true,
  };
  const integrationGroups = [
    ...(sourceTemplate.groups || []).filter((group) => group?.id !== integrationApprovalGroup.id),
    integrationApprovalGroup,
  ];
  const integrationFields = [
    ...(sourceTemplate.fields || []).filter((field) => field?.id !== integrationApprovalField.id),
    integrationApprovalField,
  ];
  return {
    ...sourceTemplate,
    groups: integrationGroups,
    integration: {
      templateId: sourceTemplate.id,
      moduleKey: config.moduleKey,
      moduleLabel: config.moduleLabel,
      action: sourceTemplate.action,
      requiredFieldIds: sourceTemplate.fields.filter((field) => field.required).map((field) => field.id),
      uniqueFields: config.uniqueFields,
      defaultValues: ['module-student-management-student', 'module-attendee-management-attendee'].includes(sourceTemplate.id) ? { status: 'IN_PROCESS' } : sourceTemplate.id === 'module-inventory-item' ? { active: true } : {},
    },
    fields: integrationFields.map((field) => {
      const moduleField = field.moduleField || config.fields[field.id];
      return {
        ...field,
        moduleBinding: moduleField
          ? {
              moduleKey: config.moduleKey,
              moduleField,
              integrationRequired: Boolean(field.required),
              unique: config.uniqueFields.includes(moduleField),
            }
          : null,
      };
    }),
  };
};

const CUSTOM_TEMPLATE_STORAGE_KEY = 'bpm-custom-form-templates';

const createField = (field = {}) => ({
  label: field.label ?? '',
  description: typeof field.description === 'string' ? field.description : '',
  id: field.id ?? field.name ?? '',
  type: field.type ?? 'text',
  mask: field.mask,
  size: typeof field.size === 'number' ? field.size : 12,
  decimalPlaces: typeof field.decimalPlaces === 'number' ? field.decimalPlaces : 2,
  groupId: field.groupId ?? '',
  options: Array.isArray(field.options)
    ? field.options.map((option) => String(option).trim()).filter((option) => option)
    : [],
  allowOtherOption: Boolean(field.allowOtherOption),
  otherOptionLabel: String(field.otherOptionLabel || '').trim() || 'Outra opção',
  otherOptionInput: ['letters', 'numbers', 'alphanumeric'].includes(field.otherOptionInput) ? field.otherOptionInput : 'all',
  otherOptionUppercase: Boolean(field.otherOptionUppercase),
  otherOptionAllowAccents: field.otherOptionAllowAccents !== false,
  required: !!field.required,
  allowAiAnswer: typeof field.allowAiAnswer === 'boolean' ? field.allowAiAnswer : false,
  visibleWhen: typeof field.visibleWhen === 'string' ? field.visibleWhen.trim() : '',
  allowMultiple: typeof field.allowMultiple === 'boolean' ? field.allowMultiple : false,
  fileCategory: typeof field.fileCategory === 'string' ? field.fileCategory.trim() : '',
  fileNameTemplate:
    typeof field.fileNameTemplate === 'string' ? field.fileNameTemplate : '',
  autoFillSource: typeof field.autoFillSource === 'string' ? field.autoFillSource : '',
  autoFillEditable: typeof field.autoFillEditable === 'boolean' ? field.autoFillEditable : true,
  tableColumns: Array.isArray(field.tableColumns)
    ? field.tableColumns
        .map((column, index) => ({
          id: typeof column?.id === 'string' && column.id.trim() ? column.id.trim() : `coluna_${index + 1}`,
          label: typeof column?.label === 'string' ? column.label.trim() : '',
          type: typeof column?.type === 'string' ? column.type : 'text',
          required: Boolean(column?.required),
          fileCategory: typeof column?.fileCategory === 'string' ? column.fileCategory.trim() : '',
          ...(typeof column?.moduleField === 'string' ? { moduleField: column.moduleField } : {}),
          ...(column?.moduleBinding && typeof column.moduleBinding === 'object' ? { moduleBinding: { ...column.moduleBinding } } : {}),
          options: Array.isArray(column?.options) ? column.options.map((option) => String(option).trim()).filter((option) => option) : [],
        }))
        .filter((column) => column.label)
    : [],
  linkedSourceType: field.linkedModuleKey || field.linkedSourceType === 'module' ? 'module' : 'process',
  linkedModuleKey: field.linkedModuleKey || '',
  moduleMappings: Array.isArray(field.moduleMappings) ? field.moduleMappings.map(item => ({ source: String(item.source || ''), target: String(item.target || '') })) : [],
  customerFilters: field.customerFilters && typeof field.customerFilters === 'object' && !Array.isArray(field.customerFilters)
    ? { status: field.customerFilters.status || 'ALL', kind: field.customerFilters.kind || 'ALL', state: field.customerFilters.state || '', city: field.customerFilters.city || '' }
    : undefined,
  linkedProcessId: field.linkedProcessId ?? '',
  linkedFormId: field.linkedFormId ?? '',
  linkedFieldId: field.linkedFieldId ?? '',
  linkedFieldMappings: Array.isArray(field.linkedFieldMappings)
    ? field.linkedFieldMappings
        .map((mapping) => ({
          sourceFieldId: typeof mapping?.sourceFieldId === 'string' ? mapping.sourceFieldId : '',
          targetFieldId: typeof mapping?.targetFieldId === 'string' ? mapping.targetFieldId : '',
        }))
        .filter((mapping) => mapping.sourceFieldId || mapping.targetFieldId)
    : [],
  sourceStatus: field.sourceStatus === 'active_and_completed' ? 'active_and_completed' : 'completed',
  multiple: typeof field.multiple === 'boolean' ? field.multiple : false,
  displayMode: field.displayMode ?? 'select',
  academicOptions: field.academicOptions,
  academicKind: field.academicKind,
  academicParentFieldId: field.academicParentFieldId,
  academicModule: field.academicModule,
  moduleBinding: field.moduleBinding && typeof field.moduleBinding === 'object'
    ? { ...field.moduleBinding }
    : null,
  signature: field.signature && typeof field.signature === 'object' ? { ...field.signature } : field.type === 'signature-attachment' ? { enabled: true, stageSigning: true, maxSignatures: 1, signerMode: 'CURRENT_USER', signers: [{ currentUser: true }] } : undefined,
  documentTemplate: field.documentTemplate && typeof field.documentTemplate === 'object'
    ? {
        title: typeof field.documentTemplate.title === 'string' ? field.documentTemplate.title : '',
        content: sanitizeDocumentTemplateHtml(field.documentTemplate.content),
      }
    : { title: '', content: '' },
});

const EXPRESSION_BUILDER_DEFAULT = {
  fieldId: '',
  operator: '===',
  value: '',
  joiner: '&&',
  valueType: 'text',
};

const slugify = (value, fallback) => {
  const normalized = (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return normalized || fallback;
};

const MAX_HISTORY_ENTRIES = 50;

const cloneField = (field) => ({
  ...field,
  options: Array.isArray(field.options) ? [...field.options] : [],
  tableColumns: Array.isArray(field.tableColumns) ? field.tableColumns.map((column) => ({ ...column })) : [],
  linkedFieldMappings: Array.isArray(field.linkedFieldMappings)
    ? field.linkedFieldMappings.map((mapping) => ({ ...mapping }))
    : [],
});

const cloneGroup = (group) => ({ ...group });

const areArraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

const OPERATOR_OPTIONS_BY_VALUE_TYPE = {
  text: ['===', '!=='],
  number: ['===', '!==', '>', '>=', '<', '<='],
  boolean: ['===', '!=='],
};

const EXPRESSION_OPERATOR_LABELS = {
  '===': 'é igual a',
  '!==': 'é diferente de',
  '>': 'é maior que',
  '>=': 'é maior ou igual a',
  '<': 'é menor que',
  '<=': 'é menor ou igual a',
};

const EXPRESSION_JOINER_LABELS = {
  '&&': 'E — todas as condições devem ser verdadeiras',
  '||': 'OU — qualquer condição pode ser verdadeira',
};

const guessValueTypeFromFieldType = (type) => {
  if (!type) return 'text';
  if (type === 'number' || type === 'decimal') return 'number';
  if (type === 'checkbox' || type === 'toggle' || type === 'boolean') return 'boolean';
  return 'text';
};

const createGroup = (group = {}) => ({
  label: group.label ?? '',
  id: group.id ?? '',
});

const getFieldTypeLabel = (type) => FIELD_TYPE_METADATA[type]?.label || type;

const AUTO_FILL_OPTIONS = [
  { value: '', label: 'Sem preenchimento automático' },
  { value: 'user_name', label: 'Nome do usuário logado' },
  { value: 'today_date', label: 'Data de hoje' },
  { value: 'user_email', label: 'E-mail do usuário logado' },
  { value: 'user_cpf', label: 'CPF do usuário logado' },
  { value: 'user_birth_date', label: 'Data de nascimento do usuário' },
  { value: 'user_sector', label: 'Setor do usuário' },
];

const TABLE_COLUMN_TYPE_OPTIONS = [
  { value: 'text', label: 'Texto' },
  { value: 'email', label: 'E-mail' },
  { value: 'date', label: 'Data' },
  { value: 'number', label: 'Número inteiro' },
  { value: 'decimal', label: 'Número decimal' },
  { value: 'file', label: 'Anexo' },
];

const renderHtmlElementToPdf = async (doc, element, {
  x,
  y,
  width,
  pageHeight,
  topMargin,
  bottomMargin = 60,
}) => {
  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });
  const canvasToPdfRatio = width / canvas.width;
  let sourceY = 0;
  let targetY = y;
  let availableHeight = pageHeight - targetY - bottomMargin;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  const findSafeCutY = (desiredHeight) => {
    const minHeight = Math.max(1, Math.floor(desiredHeight * 0.72));
    const start = Math.max(sourceY + minHeight, sourceY);
    const end = Math.min(sourceY + desiredHeight, canvas.height);
    if (end >= canvas.height) return canvas.height - sourceY;
    const step = Math.max(1, Math.floor(canvas.width / 80));
    for (let row = end; row >= start; row -= 1) {
      const pixels = context.getImageData(0, row, canvas.width, 1).data;
      let ink = 0;
      for (let column = 0; column < canvas.width; column += step) {
        const index = column * 4;
        if (pixels[index + 3] > 20 && (pixels[index] < 245 || pixels[index + 1] < 245 || pixels[index + 2] < 245)) {
          ink += 1;
          if (ink > 1) break;
        }
      }
      if (ink <= 1) return row - sourceY;
    }
    return desiredHeight;
  };

  while (sourceY < canvas.height) {
    if (availableHeight <= 0) {
      doc.addPage();
      targetY = topMargin;
      availableHeight = pageHeight - targetY - bottomMargin;
    }
    const desiredSliceHeight = Math.min(canvas.height - sourceY, Math.floor(availableHeight / canvasToPdfRatio));
    const sourceSliceHeight = findSafeCutY(desiredSliceHeight);
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = sourceSliceHeight;
    pageCanvas.getContext('2d').drawImage(canvas, 0, sourceY, canvas.width, sourceSliceHeight, 0, 0, canvas.width, sourceSliceHeight);
    const sliceHeight = sourceSliceHeight * canvasToPdfRatio;
    doc.addImage(pageCanvas.toDataURL('image/png'), 'PNG', x, targetY, width, sliceHeight);
    sourceY += sourceSliceHeight;
    if (sourceY < canvas.height) {
      doc.addPage();
      targetY = topMargin;
      availableHeight = pageHeight - targetY - bottomMargin;
    }
  }
};


export default function FormBuilder({
  formId,
  onSaved,
  openTab,
  closeTab,
  activateTab,
  returnToTabId = 'forms',
  initialSuggestion = null,
}) {
  const params = useParams();
  const routeId = params.id;
  const id = formId ?? routeId;

  const editing = id && id !== 'new';
  const [name, setName] = useState('');
  const [fields, setFieldsState] = useState([]);
  const [groups, setGroupsState] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAnchor, setModalAnchor] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [fieldForm, setFieldForm] = useState(() => createField());
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalAnchor, setGroupModalAnchor] = useState(null);
  const [groupEditingIndex, setGroupEditingIndex] = useState(null);
  const [groupForm, setGroupForm] = useState(() => createGroup());
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateModalAnchor, setTemplateModalAnchor] = useState(null);
  const [moduleIntegrationModalOpen, setModuleIntegrationModalOpen] = useState(false);
  const [pendingIntegrationTemplate, setPendingIntegrationTemplate] = useState(null);
  const [moduleIntegrationModalAnchor, setModuleIntegrationModalAnchor] = useState(null);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '' });
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [externalAccessEnabled, setExternalAccessEnabled] = useState(false);
  const [externalAccessToken, setExternalAccessToken] = useState(null);
  const [showCompanyHeader, setShowCompanyHeader] = useState(true);
  const [linkedFieldSources, setLinkedFieldSources] = useState({ processes: [], forms: [] });
  const [loadingLinkedFieldSources, setLoadingLinkedFieldSources] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [fieldClipboard, setFieldClipboard] = useState(null);
  const [expressionBuilder, setExpressionBuilder] = useState(EXPRESSION_BUILDER_DEFAULT);
  const aiDocument = useFormAiDocument();
  const [aiCoCreationOpen, setAiCoCreationOpen] = useState(false);
  const [aiCoCreationPrompt, setAiCoCreationPrompt] = useState('');
  const [aiCoCreationError, setAiCoCreationError] = useState('');
  const [aiCoCreationLoading, setAiCoCreationLoading] = useState(false);
  const [aiCoCreationSuggestion, setAiCoCreationSuggestion] = useState(null);
  const [aiCoCreationSelectedFieldIds, setAiCoCreationSelectedFieldIds] = useState([]);
  const [aiCoCreationSelectedGroupIds, setAiCoCreationSelectedGroupIds] = useState([]);
  const fileNameTemplateRef = useRef(null);
  const documentEditorRef = useRef(null);
  const [documentEditorExpanded, setDocumentEditorExpanded] = useState(false);
  const [documentAlignment, setDocumentAlignment] = useState('justify');
  const initialSchemaRef = useRef({
    serialized: JSON.stringify({ fields: [], groups: [], settings: { showCompanyHeader: true, moduleIntegration: null } }),
  });
  const [schemaChangeModalOpen, setSchemaChangeModalOpen] = useState(false);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [moduleIntegration, setModuleIntegration] = useState(null);
  const moduleIntegrationRef = useRef(moduleIntegration);
  moduleIntegrationRef.current = moduleIntegration;
  const [studentModuleOptions, setStudentModuleOptions] = useState({ cras: [], schools: [], exitReasons: [], courses: [], classes: [], fileCategories: [] });
  const [attendeeModuleOptions, setAttendeeModuleOptions] = useState({ cras: [], schools: [], exitReasons: [], groups: [], classes: [], fileCategories: [] });
  const isAcademicField = isIntegratedAcademicField(fieldForm);
  const academicCourseField = isAcademicField && integratedAcademicKind(fieldForm) === 'class'
    ? fields.find(field => integratedAcademicModule(field) === integratedAcademicModule(fieldForm) && integratedAcademicKind(field) === 'course' && (!fieldForm.academicParentFieldId || field.id === fieldForm.academicParentFieldId))
    : null;
  const attachmentModuleKey = fieldForm.moduleBinding?.moduleKey || moduleIntegration?.moduleKey;
  const isPersonAttachmentIntegration = ['students', 'attendees'].includes(attachmentModuleKey);
  const [attachmentCategories, setAttachmentCategories] = useState({ options: [], loading: false, error: false });

  useEffect(() => {
    if (!modalOpen || !isPersonAttachmentIntegration) return;
    let active = true;
    setAttachmentCategories({ options: [], loading: true, error: false });
    const endpoint = attachmentModuleKey === 'students' ? '/student-management/settings' : '/attendee-management/settings';
    api.get(endpoint).then(({ data }) => {
      if (!active) return;
      const options = Array.isArray(data?.fileCategoryOptions) ? data.fileCategoryOptions.filter(item => typeof item === 'string').map(item => item.trim()).filter(Boolean) : [];
      setAttachmentCategories({ options: [...new Set(options)], loading: false, error: false });
    }).catch(() => { if (active) setAttachmentCategories({ options: [], loading: false, error: true }); });
    return () => { active = false; };
  }, [modalOpen, attachmentModuleKey, isPersonAttachmentIntegration]);
  const [inventoryModuleOptions, setInventoryModuleOptions] = useState({ categories: [], warehouses: [] });
  const [talentBankJobOptions, setTalentBankJobOptions] = useState([]);
  const fieldTypeOptions = Object.entries(FIELD_TYPE_METADATA);
  const defaultGroupId = groups.length === 1 ? groups[0].id : '';
  const documentAlignmentOptions = useMemo(() => ([
    { command: 'justifyFull', value: 'justify', label: 'Justificado', icon: FaAlignJustify },
    { command: 'justifyLeft', value: 'left', label: 'Esquerda', icon: FaAlignLeft },
    { command: 'justifyCenter', value: 'center', label: 'Centralizar', icon: FaAlignCenter },
    { command: 'justifyRight', value: 'right', label: 'Direita', icon: FaAlignRight },
  ]), []);
  const selectedDocumentAlignment = documentAlignmentOptions.find((option) => option.value === documentAlignment) || documentAlignmentOptions[0];
  const SelectedDocumentAlignmentIcon = selectedDocumentAlignment.icon;
  const suggestionAppliedRef = useRef(false);
  const [linkedProcess, setLinkedProcess] = useState(null);
  const hasLinkedProcess = Boolean(linkedProcess);
  const fieldsRef = useRef(fields);
  const groupsRef = useRef(groups);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const historyEnabledRef = useRef(true);
  const isRestoringRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const specialTemplateTokens = useMemo(
    () => [
      { id: 'data_atual', label: 'Data atual' },
      { id: 'hora_atual', label: 'Hora atual' },
    ],
    []
  );
  const allFieldsForPreview = useMemo(() => {
    const map = new Map();
    fields.forEach((field) => {
      if (field?.id) {
        map.set(field.id, field);
      }
    });
    if (fieldForm?.id) {
      map.set(fieldForm.id, { ...map.get(fieldForm.id), ...fieldForm });
    }
    return Array.from(map.values());
  }, [fields, fieldForm]);
  const availableTemplateFields = useMemo(
    () => collectTemplateFields(allFieldsForPreview, fieldForm.id),
    [allFieldsForPreview, fieldForm.id]
  );
  const sampleValuesForPreview = useMemo(() => {
    const sample = {};
    allFieldsForPreview.forEach((field) => {
      if (field?.id) {
        sample[field.id] = getSampleValueForField(field);
      }
    });
    return sample;
  }, [allFieldsForPreview]);
  const refreshInventoryIntegratedOptions = useCallback(async () => {
    const [categoriesRes, warehousesRes] = await Promise.all([
      api.get('/inventory/categories').catch(() => ({ data: [] })),
      api.get('/inventory/warehouses').catch(() => ({ data: [] })),
    ]);
    const normalizeResponse = (response) => (Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : []);
    const nextOptions = {
      categories: normalizeResponse(categoriesRes).map((item) => ({ value: item.id, label: item.name })),
      warehouses: normalizeResponse(warehousesRes).map((item) => ({ value: item.id, label: item.name })),
    };
    setInventoryModuleOptions(nextOptions);
    return nextOptions;
  }, []);

  const updateDocumentTemplate = useCallback((patch) => {
    setFieldForm((prev) => ({
      ...prev,
      documentTemplate: {
        ...(prev.documentTemplate || {}),
        ...patch,
      },
    }));
  }, []);

  const syncDocumentEditorContent = useCallback(() => {
    const html = sanitizeDocumentTemplateHtml(documentEditorRef.current?.innerHTML || '');
    updateDocumentTemplate({ content: html });
  }, [updateDocumentTemplate]);

  const focusDocumentEditor = useCallback(() => {
    if (documentEditorRef.current) {
      documentEditorRef.current.focus();
    }
  }, []);

  const applyDocumentCommand = useCallback((command, value = null) => {
    focusDocumentEditor();
    document.execCommand(command, false, value);
    syncDocumentEditorContent();
  }, [focusDocumentEditor, syncDocumentEditorContent]);

  const applyDocumentMarker = useCallback(() => {
    focusDocumentEditor();
    const before = documentEditorRef.current?.innerHTML || '';
    document.execCommand('insertUnorderedList');
    const after = documentEditorRef.current?.innerHTML || '';
    if (after === before) {
      document.execCommand('insertHTML', false, '<ul><li>&nbsp;</li></ul>');
    }
    syncDocumentEditorContent();
  }, [focusDocumentEditor, syncDocumentEditorContent]);

  const insertDocumentHtml = useCallback((html) => {
    focusDocumentEditor();
    document.execCommand('insertHTML', false, sanitizeDocumentTemplateHtml(html));
    syncDocumentEditorContent();
  }, [focusDocumentEditor, syncDocumentEditorContent]);

  const handleDocumentPaste = useCallback((event) => {
    const clipboardHtml = event.clipboardData?.getData('text/html') || '';
    if (!clipboardHtml) return;
    event.preventDefault();
    insertDocumentHtml(clipboardHtml);
  }, [insertDocumentHtml]);

  const handleDocumentFontSize = useCallback((event) => {
    const size = event.target.value;
    if (!size) return;
    applyDocumentCommand('fontSize', size);
    event.target.value = '';
  }, [applyDocumentCommand]);

  const handleDocumentColor = useCallback((event) => {
    applyDocumentCommand('foreColor', event.target.value);
  }, [applyDocumentCommand]);

  const handleDocumentFontFamily = useCallback((event) => {
    const font = event.target.value;
    if (!font) return;
    applyDocumentCommand('fontName', font);
  }, [applyDocumentCommand]);

  const cycleDocumentAlignment = useCallback(() => {
    const currentIndex = documentAlignmentOptions.findIndex((option) => option.value === documentAlignment);
    const nextOption = documentAlignmentOptions[(currentIndex + 1) % documentAlignmentOptions.length];
    setDocumentAlignment(nextOption.value);
    applyDocumentCommand(nextOption.command);
  }, [applyDocumentCommand, documentAlignment, documentAlignmentOptions]);

  useEffect(() => {
    if (modalOpen && fieldForm.type === 'document' && documentEditorRef.current) {
      documentEditorRef.current.innerHTML = sanitizeDocumentTemplateHtml(fieldForm.documentTemplate?.content || '');
      documentEditorRef.current.style.textAlign = documentAlignment;
    }
  // Recarrega o HTML salvo apenas quando o editor abre/troca de campo; durante a digitação o DOM mantém o cursor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, editingIndex, fieldForm.type]);

  const handleDocumentPreview = useCallback(async () => {
    const title = fieldForm.documentTemplate?.title || fieldForm.label || 'Documento';
    const placeholderMap = new Map(availableTemplateFields.map((field) => [field.id, field.label || field.id]));
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 48;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
    const html = sanitizeDocumentTemplateHtml(fieldForm.documentTemplate?.content || '')
      .replace(/{{\s*([^}]+)\s*}}/g, (_, key) => {
        const label = escapeHtml(placeholderMap.get(String(key).trim()) || String(key).trim());
        return `<span style="color:#dc2626;font-weight:700;">${label}</span>`;
      });
    const previewRoot = document.createElement('div');
    previewRoot.style.position = 'absolute';
    previewRoot.style.left = '0';
    previewRoot.style.top = '0';
    previewRoot.style.zIndex = '-1';
    previewRoot.style.pointerEvents = 'none';
    previewRoot.style.width = `${pageWidth - margin * 2}pt`;
    previewRoot.style.background = '#ffffff';
    previewRoot.innerHTML = `
      <style>
        .document-pdf-preview, .document-pdf-preview * { box-sizing: border-box; }
        .document-pdf-preview {
          color: #111827;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11pt;
          line-height: 1.45;
          overflow-wrap: anywhere;
          text-align: ${documentAlignment};
        }
        .document-pdf-preview h1 {
          color: #111827;
          font-size: 16pt;
          line-height: 1.2;
          margin: 0 0 14pt;
          text-align: center;
        }
        .document-pdf-preview p { margin: 0 0 11pt; }
        .document-pdf-preview div { margin: 0 0 11pt; }
        .document-pdf-preview ul, .document-pdf-preview ol { list-style-position: outside; margin: 0 0 11pt 18pt; padding-left: 18pt; }
        .document-pdf-preview li { margin: 0 0 4pt; }
      </style>
      <main class="document-pdf-preview">
        <h1>${escapeHtml(title)}</h1>
        <section>${html || '<p>Documento sem conteúdo configurado.</p>'}</section>
      </main>
    `;
    document.body.appendChild(previewRoot);
    try {
      await renderHtmlElementToPdf(doc, previewRoot, {
        x: margin,
        y: margin,
        width: pageWidth - margin * 2,
        pageHeight,
        topMargin: margin,
        bottomMargin: 90,
      });
    } finally {
      document.body.removeChild(previewRoot);
    }
    const total = doc.getNumberOfPages();
    for (let index = 1; index <= total; index += 1) {
      doc.setPage(index);
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text('Documento gerado pelo sistema Intelligent BPM Nash.', pageWidth / 2, pageHeight - 26, { align: 'center' });
      doc.text(`${index}/${total}`, pageWidth - margin, pageHeight - 26, { align: 'right' });
    }
    doc.save(`previa-${fieldForm.id || 'documento'}.pdf`);
  }, [availableTemplateFields, documentAlignment, fieldForm.documentTemplate?.content, fieldForm.documentTemplate?.title, fieldForm.id, fieldForm.label]);

  const fileNamePreview = useMemo(() => {
    if (!fieldForm.fileNameTemplate || !fieldForm.fileNameTemplate.trim()) {
      return '';
    }
    return (
      buildAttachmentFileName(fieldForm.fileNameTemplate, {
        values: sampleValuesForPreview,
        fields: allFieldsForPreview,
        originalFileName: 'documento.pdf',
      }) || ''
    );
  }, [fieldForm.fileNameTemplate, allFieldsForPreview, sampleValuesForPreview]);
  const insertFileNamePlaceholder = useCallback((token) => {
    if (!token) return;
    const snippet = `{{${token}}}`;
    setFieldForm((prev) => {
      const current = prev.fileNameTemplate || '';
      const textarea = fileNameTemplateRef.current;
      if (
        textarea &&
        typeof textarea.selectionStart === 'number' &&
        typeof textarea.selectionEnd === 'number'
      ) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const next = current.slice(0, start) + snippet + current.slice(end);
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => {
            textarea.focus();
            const cursor = start + snippet.length;
            textarea.setSelectionRange(cursor, cursor);
          });
        }
        return { ...prev, fileNameTemplate: next };
      }
      const next = current ? `${current} ${snippet}` : snippet;
      return { ...prev, fileNameTemplate: next };
    });
  }, []);

  const pushSnapshot = useCallback(
    (fieldsSnapshot, groupsSnapshot) => {
      if (!historyEnabledRef.current || isRestoringRef.current) return;
      let history = historyRef.current.slice(0, historyIndexRef.current + 1);
      history.push({
        fields: fieldsSnapshot.map(cloneField),
        moduleIntegration: moduleIntegrationRef.current,
        groups: groupsSnapshot.map(cloneGroup),
      });
      if (history.length > MAX_HISTORY_ENTRIES) {
        history = history.slice(history.length - MAX_HISTORY_ENTRIES);
      }
      historyRef.current = history;
      historyIndexRef.current = history.length - 1;
      setCanUndo(historyIndexRef.current >= 0);
    },
    [setCanUndo]
  );

  const captureHistorySnapshot = useCallback(() => {
    pushSnapshot(fieldsRef.current, groupsRef.current);
  }, [pushSnapshot]);

  const resetHistory = useCallback(() => {
    historyRef.current = [];
    historyIndexRef.current = -1;
    historyEnabledRef.current = true;
    setCanUndo(false);
  }, [setCanUndo]);

  const setFields = useCallback(
    (updater, options = {}) => {
      setFieldsState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (
          !options.skipHistory &&
          !isRestoringRef.current &&
          historyEnabledRef.current &&
          !areArraysEqual(prev, next)
        ) {
          pushSnapshot(prev, groupsRef.current);
        }
        return next;
      });
    },
    [pushSnapshot]
  );

  const setGroups = useCallback(
    (updater, options = {}) => {
      setGroupsState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (
          !options.skipHistory &&
          !isRestoringRef.current &&
          historyEnabledRef.current &&
          !areArraysEqual(prev, next)
        ) {
          pushSnapshot(fieldsRef.current, prev);
        }
        return next;
      });
    },
    [pushSnapshot]
  );

  const undo = useCallback(() => {
    if (historyIndexRef.current < 0) return;
    const snapshot = historyRef.current[historyIndexRef.current];
    historyIndexRef.current -= 1;
    setCanUndo(historyIndexRef.current >= 0);
    isRestoringRef.current = true;
    setFieldsState(snapshot.fields.map(cloneField));
    setGroupsState(snapshot.groups.map(cloneGroup));
    setModuleIntegration(snapshot.moduleIntegration || null);
    Promise.resolve().then(() => {
      isRestoringRef.current = false;
    });
  }, [setCanUndo]);
  const getCurrentSchemaSignature = useCallback(
    () => JSON.stringify({ fields, groups, settings: { showCompanyHeader, moduleIntegration } }),
    [fields, groups, showCompanyHeader, moduleIntegration]
  );
  const selectedExpressionField = useMemo(
    () => fields.find((f) => f.id === expressionBuilder.fieldId) || null,
    [fields, expressionBuilder.fieldId]
  );
  const expressionBuilderOperators = OPERATOR_OPTIONS_BY_VALUE_TYPE[expressionBuilder.valueType] || OPERATOR_OPTIONS_BY_VALUE_TYPE.text;
  const expressionBuilderFieldOptions = fieldChoiceOptions(selectedExpressionField);
  const canInsertExpression = Boolean(
    expressionBuilder.fieldId &&
      (expressionBuilder.valueType === 'boolean' || String(expressionBuilder.value ?? '').trim() !== '')
  );
  const isFieldSizeInvalid =
    !Number.isInteger(fieldForm.size) || fieldForm.size < 1 || fieldForm.size > 12;

  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  useEffect(() => {
    if (!moduleIntegration?.requiredFieldIds?.length) return;
    const currentFieldIds = new Set(fields.map((field) => field.id));
    const missingRequired = moduleIntegration.requiredFieldIds.filter((fieldId) => !currentFieldIds.has(fieldId));
    if (!missingRequired.length) return;
    alert('Um campo obrigatório da integração foi removido. A integração será cancelada e o formulário será zerado.');
    setFields([], { skipHistory: true });
    setGroups([], { skipHistory: true });
    setName('');
    setModuleIntegration(null);
  }, [fields, moduleIntegration, setFields, setGroups]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(CUSTOM_TEMPLATE_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            setCustomTemplates(parsed);
          }
        }
      } catch {
        setCustomTemplates([]);
      }
    }
  }, []);

  useEffect(() => {
    resetHistory();
    if (editing) {
      api.get(`/forms/${id}`)
        .then(res => {
          setName(res.data.name);
          const schema = res.data.schema || {};
          const loadedFields = Array.isArray(schema.fields) ? schema.fields : [];
          const normalizedFields = hydrateIntegratedFamilyFields(loadedFields).map((f) => createField(f));
          setFields(normalizedFields, { skipHistory: true });
          const loadedGroups = Array.isArray(schema.groups) ? schema.groups : [];
          const normalizedGroups = loadedGroups.map((g) => createGroup(g));
          setGroups(normalizedGroups, { skipHistory: true });
          setShowCompanyHeader(schema?.settings?.showCompanyHeader !== false);
          setModuleIntegration(schema?.settings?.moduleIntegration || null);
          initialSchemaRef.current = {
            serialized: JSON.stringify({
              fields: normalizedFields,
              groups: normalizedGroups,
              settings: {
                showCompanyHeader: schema?.settings?.showCompanyHeader !== false,
                moduleIntegration: schema?.settings?.moduleIntegration || null,
              },
            }),
          };
          setExternalAccessEnabled(Boolean(res.data.externalAccessEnabled));
          setExternalAccessToken(res.data.externalAccessToken || null);
          const processInfo = res.data.linkedProcessId
            ? {
                id: res.data.linkedProcessId,
                name: res.data.linkedProcessName || 'Processo vinculado',
              }
            : null;
          setLinkedProcess(processInfo);
        })
        .catch(() => {});
    } else {
      setLinkedProcess(null);
    }
  }, [id, editing, resetHistory, setFields, setGroups]);

  useEffect(() => {
    if (editing || !initialSuggestion || suggestionAppliedRef.current) return;
    const schema = initialSuggestion.schema || {};
    if (initialSuggestion.name) {
      setName(initialSuggestion.name);
    }
    if (Array.isArray(schema.groups)) {
      setGroups(schema.groups.map((group) => createGroup(group)), { skipHistory: true });
    }
    if (Array.isArray(schema.fields)) {
      setFields(schema.fields.map((field) => createField(field)), { skipHistory: true });
    }
    setExternalAccessEnabled(false);
    setExternalAccessToken(null);
    setShowCompanyHeader(true);
    setLinkedProcess(null);
    setModuleIntegration(null);
    suggestionAppliedRef.current = true;
    resetHistory();
  }, [editing, initialSuggestion, resetHistory, setFields, setGroups]);

  useEffect(() => {
    if (!moduleIntegrationModalOpen) return;
    let active = true;
    Promise.all([
      api.get('/student-management/settings').catch(() => ({ data: null })),
      api.get('/student-management/courses').catch(() => ({ data: [] })),
      api.get('/student-management/classes').catch(() => ({ data: [] })),
      api.get('/attendee-management/settings').catch(() => ({ data: null })),
      api.get('/attendee-management/groups').catch(() => ({ data: [] })),
      api.get('/attendee-management/classes').catch(() => ({ data: [] })),
      refreshInventoryIntegratedOptions(),
      api.get('/human-resources/talent-jobs').catch(() => ({ data: [] })),
    ]).then(([settingsRes, coursesRes, classesRes, attendeeSettingsRes, attendeeGroupsRes, attendeeClassesRes, , talentJobsRes]) => {
      if (!active) return;
      const settings = settingsRes.data || {};
      setStudentModuleOptions({
        cras: Array.isArray(settings.crasOptions) ? settings.crasOptions : [],
        schools: Array.isArray(settings.schoolOptions) ? settings.schoolOptions : [],
        exitReasons: Array.isArray(settings.exitReasonOptions) ? settings.exitReasonOptions : [],
        courses: Array.isArray(coursesRes.data) ? coursesRes.data.map((item) => ({ value: item.id, label: item.name })) : [],
        classes: Array.isArray(classesRes.data) ? classesRes.data.map((item) => ({ value: String(item.id), label: item.name, parentId: String(item.courseId ?? item.groupId ?? '') })) : [],
        fileCategories: Array.isArray(settings.fileCategoryOptions) ? settings.fileCategoryOptions : [],
      });
      const attendeeSettings = attendeeSettingsRes.data || {};
      setAttendeeModuleOptions({
        cras: Array.isArray(attendeeSettings.crasOptions) ? attendeeSettings.crasOptions : [],
        schools: Array.isArray(attendeeSettings.schoolOptions) ? attendeeSettings.schoolOptions : [],
        exitReasons: Array.isArray(attendeeSettings.exitReasonOptions) ? attendeeSettings.exitReasonOptions : [],
        groups: Array.isArray(attendeeGroupsRes.data) ? attendeeGroupsRes.data.map((item) => ({ value: item.id, label: item.name })) : [],
        classes: Array.isArray(attendeeClassesRes.data) ? attendeeClassesRes.data.map((item) => ({ value: String(item.id), label: item.name, parentId: String(item.courseId ?? item.groupId ?? '') })) : [],
        fileCategories: Array.isArray(attendeeSettings.fileCategoryOptions) ? attendeeSettings.fileCategoryOptions : [],
      });
      const talentJobs = Array.isArray(talentJobsRes.data) ? talentJobsRes.data : [];
      setTalentBankJobOptions(talentJobs.filter((job) => job?.status === 'Em andamento').map((job) => String(job?.name || '').trim()).filter(Boolean));
    });
    return () => { active = false; };
  }, [moduleIntegrationModalOpen, refreshInventoryIntegratedOptions]);

  useEffect(() => {
    if (moduleIntegration?.moduleKey !== 'inventory') return;
    let active = true;
    refreshInventoryIntegratedOptions().then((options) => {
      if (!active) return;
      setFields((currentFields) => currentFields.map((field) => {
        if (field.id === 'inventory_categoryId') return { ...field, options: options.categories.map((item) => `${item.value} - ${item.label}`) };
        if (field.id === 'inventory_warehouseId') return { ...field, options: options.warehouses.map((item) => `${item.value} - ${item.label}`) };
        return field;
      }), { skipHistory: true });
    });
    return () => { active = false; };
  }, [moduleIntegration?.moduleKey, refreshInventoryIntegratedOptions, setFields]);

  useEffect(() => {
    if (moduleIntegration?.moduleKey !== 'talentBank') return;
    let active = true;
    api.get('/human-resources/talent-jobs').then((response) => {
      if (!active) return;
      const jobs = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];
      const options = jobs
        .filter((job) => job?.status === 'Em andamento')
        .map((job) => String(job?.name || '').trim())
        .filter(Boolean);
      setTalentBankJobOptions(options);
      setFields((currentFields) => currentFields.map((field) => (
        field.id === 'talent_position' ? { ...field, type: 'list', options } : field
      )), { skipHistory: true });
    }).catch(() => undefined);
    return () => { active = false; };
  }, [moduleIntegration?.moduleKey, setFields]);

  useEffect(() => {
    if (!copyFeedback) return;
    const timeout = setTimeout(() => setCopyFeedback(''), 2500);
    return () => clearTimeout(timeout);
  }, [copyFeedback]);

  useEffect(() => {
    if (!modalOpen) return;
    if (fieldForm.type === 'list' && fieldForm.options.length === 0) {
      setFieldForm((prev) => ({
        ...prev,
        options: [''],
      }));
    }
  }, [modalOpen, fieldForm.type, fieldForm.options.length]);

  useEffect(() => {
    if (!modalOpen || fieldForm.type !== 'linkedField') return;
    if (linkedFieldSources.processes.length > 0) return;
    let active = true;
    setLoadingLinkedFieldSources(true);
    api
      .get('/forms/linked-field/sources')
      .then((res) => {
        if (!active) return;
        setLinkedFieldSources({
          processes: Array.isArray(res.data?.processes) ? res.data.processes : [],
          forms: Array.isArray(res.data?.forms) ? res.data.forms : [],
        });
      })
      .catch(() => {
        if (!active) return;
        setLinkedFieldSources({ processes: [], forms: [] });
      })
      .finally(() => {
        if (!active) return;
        setLoadingLinkedFieldSources(false);
      });
    return () => {
      active = false;
    };
  }, [modalOpen, fieldForm.type, linkedFieldSources.processes.length]);

  const linkedProcessOptions = useMemo(
    () => (Array.isArray(linkedFieldSources.processes) ? linkedFieldSources.processes : []),
    [linkedFieldSources.processes]
  );

  const selectedLinkedProcess = useMemo(
    () => linkedProcessOptions.find((process) => String(process.id) === String(fieldForm.linkedProcessId || '')) || null,
    [linkedProcessOptions, fieldForm.linkedProcessId]
  );

  const selectedLinkedForm = useMemo(() => {
    const formId = selectedLinkedProcess?.formId ?? fieldForm.linkedFormId;
    return (linkedFieldSources.forms || []).find((form) => String(form.id) === String(formId || '')) || null;
  }, [linkedFieldSources.forms, selectedLinkedProcess, fieldForm.linkedFormId]);

  useEffect(() => {
    const validOperators = OPERATOR_OPTIONS_BY_VALUE_TYPE[expressionBuilder.valueType] || OPERATOR_OPTIONS_BY_VALUE_TYPE.text;
    if (!validOperators.includes(expressionBuilder.operator)) {
      setExpressionBuilder((prev) => ({
        ...prev,
        operator: validOperators[0],
      }));
    }
  }, [expressionBuilder.valueType, expressionBuilder.operator]);

  useEffect(() => {
    if (expressionBuilder.valueType === 'boolean' && !['true', 'false'].includes(expressionBuilder.value)) {
      setExpressionBuilder((prev) => ({
        ...prev,
        value: 'true',
      }));
    }
  }, [expressionBuilder.valueType, expressionBuilder.value]);

  const persistCustomTemplates = (templates) => {
    setCustomTemplates(templates);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(CUSTOM_TEMPLATE_STORAGE_KEY, JSON.stringify(templates));
      } catch {
        // ignore storage errors
      }
    }
  };

  const moduleIntegrationTemplates = useMemo(() => MODULE_INTEGRATION_TEMPLATES.map(withModuleBindings).map((template) => {
    if (template.id === 'module-inventory-item') {
      return {
        ...template,
        fields: template.fields.map((field) => {
          if (field.id === 'inventory_categoryId') return { ...field, options: inventoryModuleOptions.categories.map((item) => `${item.value} - ${item.label}`) };
          if (field.id === 'inventory_warehouseId') return { ...field, options: inventoryModuleOptions.warehouses.map((item) => `${item.value} - ${item.label}`) };
          return field;
        }),
      };
    }
    if (template.id === 'module-hr-talent-bank') {
      return {
        ...template,
        fields: template.fields.map((field) => (
          field.id === 'talent_position' ? { ...field, type: 'list', options: talentBankJobOptions } : field
        )),
      };
    }
    if (template.id === 'module-attendee-management-attendee') {
      return {
        ...template,
        fields: template.fields.map((field) => {
          if (field.id === 'attendee_cras') return { ...field, type: 'list', options: attendeeModuleOptions.cras };
          if (field.id === 'attendee_school') return { ...field, type: 'list', options: attendeeModuleOptions.schools };
          if (field.id === 'attendee_exitReason') return { ...field, type: 'list', options: attendeeModuleOptions.exitReasons };
          if (field.id === 'attendee_groupId') return { ...field, academicOptions: attendeeModuleOptions.groups, academicKind: 'course', academicModule: 'attendees' };
          if (field.id === 'attendee_classId') return { ...field, academicOptions: attendeeModuleOptions.classes, academicKind: 'class', academicModule: 'attendees' };
          if (field.id === 'attendee_documents') return { ...field, tableColumns: (field.tableColumns || []).map((column) => column.id === 'category' ? { ...column, options: attendeeModuleOptions.fileCategories } : column) };
          return field;
        }),
      };
    }
    if (template.id !== 'module-student-management-student') return template;
    return {
      ...template,
      fields: template.fields.map((field) => {
        if (field.id === 'student_cras') return { ...field, type: 'list', options: studentModuleOptions.cras };
        if (field.id === 'student_school') return { ...field, type: 'list', options: studentModuleOptions.schools };
        if (field.id === 'student_exitReason') return { ...field, type: 'list', options: studentModuleOptions.exitReasons };
        if (field.id === 'student_courseId') return { ...field, academicOptions: studentModuleOptions.courses, academicKind: 'course', academicModule: 'students' };
        if (field.id === 'student_classId') return { ...field, academicOptions: studentModuleOptions.classes, academicKind: 'class', academicModule: 'students' };
        if (field.id === 'student_documents') return { ...field, tableColumns: (field.tableColumns || []).map((column) => column.id === 'category' ? { ...column, options: studentModuleOptions.fileCategories } : column) };
        return field;
      }),
    };
  }), [studentModuleOptions, attendeeModuleOptions, inventoryModuleOptions, talentBankJobOptions]);
  const allTemplates = useMemo(() => [...FORM_TEMPLATES, ...customTemplates], [customTemplates]);

  const getAnchorPointFromEvent = (event) => {
    if (!event) return null;
    const target = event.currentTarget;
    if (target && typeof target.getBoundingClientRect === 'function') {
      const rect = target.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    }

    const { clientX, clientY } = event;
    if (typeof clientX === 'number' && typeof clientY === 'number') {
      return { x: clientX, y: clientY };
    }

    return null;
  };

  const openNewField = (groupId = '', type = 'text', anchorPoint = null) => {
    setFieldForm(createField({ groupId, type }));
    setEditingIndex(null);
    setModalAnchor(anchorPoint);
    setModalOpen(true);
    setExpressionBuilder(EXPRESSION_BUILDER_DEFAULT);
  };

  const openNewFieldByType = (type, groupId = '', anchorPoint = null) => {
    openNewField(groupId, type, anchorPoint);
  };

  const openEditField = (index, anchorPoint = null) => {
    const f = fields[index];
    setFieldForm(createField(f));
    setEditingIndex(index);
    setModalAnchor(anchorPoint);
    setModalOpen(true);
    setExpressionBuilder(EXPRESSION_BUILDER_DEFAULT);
  };

  const closeFieldModal = () => {
    setDocumentEditorExpanded(false);
    setModalOpen(false);
    setModalAnchor(null);
    setExpressionBuilder(EXPRESSION_BUILDER_DEFAULT);
  };

  const handleCopyField = (index) => {
    const field = fields[index];
    if (!field) return;
    const normalized = createField(field);
    setFieldClipboard(normalized);
    const displayName = normalized.label || normalized.id || 'Campo copiado';
    setToastMsg(`Campo "${displayName}" copiado. Escolha onde colar.`);
  };

  const handlePasteField = (groupId = '') => {
    if (!fieldClipboard) return;
    const targetGroup = groupId ? groups.find((group) => group.id === groupId) : null;
    setFields((prev) => {
      const rawBaseId = fieldClipboard.id ? `${fieldClipboard.id}-copia` : 'campo';
      const baseId = rawBaseId.toLowerCase().replace(/\s+/g, '') || 'campo';
      let candidateId = baseId;
      let counter = 2;
      const idExists = (value) => prev.some((field) => field.id === value);
      while (idExists(candidateId)) {
        candidateId = `${baseId}-${counter}`;
        counter += 1;
      }

      let nextLabel = fieldClipboard.label;
      if (fieldClipboard.label) {
        const baseLabel = `${fieldClipboard.label} (cópia)`;
        nextLabel = baseLabel;
        let labelCounter = 2;
        const labelExists = (value) => prev.some((field) => field.label === value);
        while (labelExists(nextLabel)) {
          nextLabel = `${baseLabel} ${labelCounter}`;
          labelCounter += 1;
        }
      }

      const newField = createField({
        ...fieldClipboard,
        id: candidateId,
        label: fieldClipboard.label ? nextLabel : fieldClipboard.label,
        groupId,
      });
      return [...prev, newField];
    });

    const displayName = fieldClipboard.label || fieldClipboard.id || 'Campo copiado';
    const destination = targetGroup ? `em "${targetGroup.label}"` : 'no formulário';
    setToastMsg(`Campo "${displayName}" colado ${destination}.`);
  };

  const clearFieldClipboard = () => {
    setFieldClipboard(null);
    setToastMsg('Área de transferência de campos limpa.');
  };

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue =
      type === 'checkbox'
        ? checked
        : name === 'size' || name === 'decimalPlaces'
        ? Number(value)
        : value;
    if (name === 'id') {
      newValue = value.toLowerCase().replace(/\s+/g, '');
    }
    setFieldForm((f) => ({
      ...f,
      [name]: newValue,
    }));
  };

  const addListOption = () => {
    setFieldForm((prev) => ({
      ...prev,
      options: [...prev.options, ''],
    }));
  };

  const updateListOption = (index, optionValue) => {
    setFieldForm((prev) => {
      const updated = [...prev.options];
      updated[index] = optionValue;
      return {
        ...prev,
        options: updated,
      };
    });
  };

  const removeListOption = (index) => {
    setFieldForm((prev) => {
      const updated = prev.options.filter((_, i) => i !== index);
      return {
        ...prev,
        options: updated.length > 0 ? updated : [''],
      };
    });
  };

  const handleExpressionBuilderFieldChange = (fieldId) => {
    const selectedField = fields.find((field) => field.id === fieldId) || null;
    const guessedValueType = guessValueTypeFromFieldType(selectedField?.type);
    const availableOperators = OPERATOR_OPTIONS_BY_VALUE_TYPE[guessedValueType] || OPERATOR_OPTIONS_BY_VALUE_TYPE.text;
    const selectedFieldOptions = fieldChoiceOptions(selectedField);
    const defaultValue =
      guessedValueType === 'boolean'
        ? 'true'
        : selectedFieldOptions[0]?.value || '';
    setExpressionBuilder((prev) => ({
      ...prev,
      fieldId,
      valueType: guessedValueType,
      operator: availableOperators.includes(prev.operator) ? prev.operator : availableOperators[0],
      value: defaultValue,
    }));
  };

  const handleExpressionBuilderChange = (name, value) => {
    setExpressionBuilder((prev) => {
      if (name === 'valueType') {
        const operatorsForType = OPERATOR_OPTIONS_BY_VALUE_TYPE[value] || OPERATOR_OPTIONS_BY_VALUE_TYPE.text;
        return {
          ...prev,
          valueType: value,
          operator: operatorsForType.includes(prev.operator) ? prev.operator : operatorsForType[0],
          value: value === 'boolean' ? (prev.value === 'false' ? 'false' : 'true') : '',
        };
      }
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const insertExpressionSnippet = () => {
    if (!canInsertExpression) return;

    const operators = OPERATOR_OPTIONS_BY_VALUE_TYPE[expressionBuilder.valueType] || OPERATOR_OPTIONS_BY_VALUE_TYPE.text;
    const operator = operators.includes(expressionBuilder.operator) ? expressionBuilder.operator : operators[0];

    let valueExpression = expressionBuilder.value;
    if (expressionBuilder.valueType === 'text') {
      const escaped = String(valueExpression ?? '').replace(/'/g, "\\'");
      valueExpression = `'${escaped}'`;
    } else if (expressionBuilder.valueType === 'boolean') {
      valueExpression = expressionBuilder.value === 'false' ? 'false' : 'true';
    }

    const snippet = `${expressionBuilder.fieldId} ${operator} ${valueExpression}`;

    setFieldForm((prev) => ({
      ...prev,
      visibleWhen: prev.visibleWhen
        ? `${prev.visibleWhen} ${expressionBuilder.joiner} ${snippet}`
        : snippet,
    }));

    setExpressionBuilder((prev) => ({
      ...prev,
      joiner: '&&',
    }));
  };

  const saveField = () => {
    if (!fieldForm.label.trim() || !fieldForm.id.trim()) {
      alert('Nome e ID do campo são obrigatórios');
      return;
    }
    if (isFieldSizeInvalid) {
      alert('A largura do campo deve estar entre 1 e 12.');
      return;
    }
    const exists = fields.some((f, i) => f.id === fieldForm.id && i !== editingIndex);
    if (exists) {
      alert('ID do campo duplicado');
      return;
    }
    const normalizedField = createField(fieldForm);
    if (normalizedField.type === 'linkedField' && normalizedField.linkedSourceType === 'module') {
      if (!normalizedField.linkedModuleKey || !normalizedField.linkedFieldId) {
        alert('Selecione o módulo e o identificador do registro.'); return;
      }
      if (normalizedField.moduleMappings.some(mapping => !mapping.source || !mapping.target || !fields.some(target => target.id === mapping.target && target.id !== normalizedField.id))) {
        alert('Complete cada vínculo com o dado de origem e o campo de destino.'); return;
      }
      if (new Set(normalizedField.moduleMappings.map(mapping => mapping.target)).size !== normalizedField.moduleMappings.length) {
        alert('Cada campo de destino deve receber apenas um dado do módulo.'); return;
      }
    }
    if (normalizedField.type === 'list' && !Array.isArray(normalizedField.academicOptions) && normalizedField.options.length === 0) {
      alert('Adicione ao menos uma opção para campos do tipo lista.');
      return;
    }
    if (normalizedField.type === 'table' && (!Array.isArray(normalizedField.tableColumns) || normalizedField.tableColumns.length === 0)) {
      alert('Adicione ao menos uma coluna para o campo de tabela.');
      return;
    }
    if (editingIndex !== null) {
      setFields((f) => f.map((field, i) => (i === editingIndex ? normalizedField : field)));
    } else {
      setFields((f) => [...f, normalizedField]);
    }
    closeFieldModal();
  };

  const deleteField = () => {
    if (editingIndex !== null) {
      setFields((f) => f.filter((_, i) => i !== editingIndex));
      closeFieldModal();
    }
  };

  const openNewGroup = (anchorPoint = null) => {
    setGroupForm(createGroup());
    setGroupEditingIndex(null);
    setGroupModalAnchor(anchorPoint);
    setGroupModalOpen(true);
  };

  const openEditGroup = (index, anchorPoint = null) => {
    setGroupForm(createGroup(groups[index]));
    setGroupEditingIndex(index);
    setGroupModalAnchor(anchorPoint);
    setGroupModalOpen(true);
  };

  const closeGroupModal = () => {
    setGroupModalOpen(false);
    setGroupModalAnchor(null);
  };

  const handleGroupChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'id') {
      v = value.toLowerCase().replace(/\s+/g, '');
    }
    setGroupForm((g) => ({ ...g, [name]: v }));
  };

  const saveGroup = () => {
    if (!groupForm.label.trim() || !groupForm.id.trim()) {
      alert('Nome e ID do grupo são obrigatórios');
      return;
    }
    const exists = groups.some((g, i) => g.id === groupForm.id && i !== groupEditingIndex);
    if (exists) {
      alert('ID do grupo duplicado');
      return;
    }
    if (groupEditingIndex !== null) {
      setGroups((gs) => gs.map((g, i) => (i === groupEditingIndex ? groupForm : g)));
    } else {
      setGroups((gs) => [...gs, groupForm]);
    }
    closeGroupModal();
  };

  const deleteGroup = () => {
    if (groupEditingIndex !== null) {
      const removedId = groups[groupEditingIndex].id;
      captureHistorySnapshot();
      setGroups((gs) => gs.filter((_, i) => i !== groupEditingIndex), { skipHistory: true });
      setFields((fs) => fs.filter((f) => f.groupId !== removedId), { skipHistory: true });
      closeGroupModal();
    }
  };

  const applyTemplate = (template) => {
    if (!template) return;
    const hasExistingData =
      (name && name.trim().length > 0) || fields.length > 0 || groups.length > 0;
    if (hasExistingData) {
      let confirmed = true;
      if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
        confirmed = window.confirm(
          'Aplicar um modelo substituirá os campos e grupos atuais. Deseja continuar?'
        );
      }
      if (!confirmed) return;
    }
    captureHistorySnapshot();
    setGroups(template.groups.map((g) => createGroup(g)), { skipHistory: true });
    setFields(template.fields.map((f) => createField(f)), { skipHistory: true });
    setModuleIntegration(null);
    if (!editing && !name.trim()) {
      setName(template.name);
    }
    closeTemplateModal();
    setToastMsg(`Modelo aplicado: ${template.name}`);
  };

  const startTemplateCreation = () => {
    setTemplateForm({ name: name?.trim() || '', description: '' });
    setIsCreatingTemplate(true);
  };

  const handleTemplateFormChange = (event) => {
    const { name: inputName, value } = event.target;
    setTemplateForm((prev) => ({ ...prev, [inputName]: value }));
  };

  const saveCurrentAsTemplate = () => {
    const normalizedName = templateForm.name.trim();
    if (!normalizedName) {
      alert('Informe um nome para o modelo.');
      return;
    }
    if (fields.length === 0) {
      alert('Adicione ao menos um campo antes de salvar como modelo.');
      return;
    }
    const newTemplate = {
      id: `custom-${Date.now()}`,
      name: normalizedName,
      description: templateForm.description.trim(),
      groups: groups.map((g) => ({ ...g })),
      fields: fields.map((f) => ({ ...f })),
      isCustom: true,
    };
    persistCustomTemplates([...customTemplates, newTemplate]);
    setIsCreatingTemplate(false);
    closeTemplateModal();
    setToastMsg(`Modelo salvo: ${normalizedName}`);
  };

  const deleteCustomTemplate = (templateId) => {
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Remover este modelo personalizado?');
      if (!confirmed) return;
    }
    persistCustomTemplates(customTemplates.filter((template) => template.id !== templateId));
  };

  const updateGroupLabel = (id, label) => {
    setGroups((gs) => gs.map((g) => (g.id === id ? { ...g, label } : g)));
  };

  const removeGroup = (id) => {
    captureHistorySnapshot();
    setGroups((gs) => gs.filter((g) => g.id !== id), { skipHistory: true });
    setFields((fs) => fs.filter((f) => f.groupId !== id), { skipHistory: true });
  };

  const moveGroup = (from, to) => {
    setGroups((gs) => {
      if (to < 0 || to >= gs.length) return gs;
      const newGs = [...gs];
      const [item] = newGs.splice(from, 1);
      newGs.splice(to, 0, item);
      return newGs;
    });
  };

  const moveGroupUp = (index) => moveGroup(index, index - 1);

  const moveGroupDown = (index) => moveGroup(index, index + 1);

  const [dragState, setDragState] = useState(null);

  const handleGroupDragStart = (groupIndex) => {
    setDragState({ type: 'group', groupIndex });
  };

  const handleFieldDragStart = (fieldIndex) => {
    setDragState({
      type: 'field',
      fieldIndex,
      sourceGroupId: fields[fieldIndex]?.groupId || '',
    });
  };

  const clearDragState = () => setDragState(null);

  const moveField = (from, to) => {
    setFields((fs) => {
      if (to < 0 || to >= fs.length) return fs;
      const newFs = [...fs];
      const [item] = newFs.splice(from, 1);
      newFs.splice(to, 0, item);
      return newFs;
    });
  };

  const findPrevSameGroup = (index) => {
    const gid = fields[index].groupId || '';
    for (let i = index - 1; i >= 0; i--) {
      if ((fields[i].groupId || '') === gid) return i;
    }
    return -1;
  };

  const findNextSameGroup = (index) => {
    const gid = fields[index].groupId || '';
    for (let i = index + 1; i < fields.length; i++) {
      if ((fields[i].groupId || '') === gid) return i;
    }
    return -1;
  };

  const moveFieldUp = (index) => {
    const target = findPrevSameGroup(index);
    if (target !== -1) moveField(index, target);
  };

  const moveFieldDown = (index) => {
    const target = findNextSameGroup(index);
    if (target !== -1) moveField(index, target);
  };

  const moveFieldToGroup = useCallback((fieldIndex, targetGroupId, targetIndex = null) => {
    setFields((currentFields) => {
      if (fieldIndex < 0 || fieldIndex >= currentFields.length) return currentFields;

      const nextFields = [...currentFields];
      const [field] = nextFields.splice(fieldIndex, 1);
      if (!field) return currentFields;

      const normalizedTargetGroupId = targetGroupId || '';
      const updatedField = { ...field, groupId: normalizedTargetGroupId || undefined };

      const insertionCandidates = nextFields
        .map((candidate, index) => ({ candidate, index }))
        .filter(({ candidate }) => (candidate.groupId || '') === normalizedTargetGroupId);

      const boundedTargetIndex = targetIndex == null
        ? insertionCandidates.length
        : Math.max(0, Math.min(targetIndex, insertionCandidates.length));

      const insertAt = boundedTargetIndex >= insertionCandidates.length
        ? nextFields.length
        : insertionCandidates[boundedTargetIndex].index;

      nextFields.splice(insertAt, 0, updatedField);
      return nextFields;
    });
  }, [setFields]);

  const handleGroupDrop = (targetIndex) => {
    if (!dragState || dragState.type !== 'group') return;
    if (dragState.groupIndex !== targetIndex) {
      moveGroup(dragState.groupIndex, targetIndex);
    }
    clearDragState();
  };

  const handleFieldDrop = (targetFieldIndex) => {
    if (!dragState || dragState.type !== 'field') return;

    const targetField = fields[targetFieldIndex];
    if (!targetField) {
      clearDragState();
      return;
    }

    const targetGroupId = targetField.groupId || '';
    const targetIndexWithinGroup = fields
      .filter((field, index) => index !== dragState.fieldIndex && (field.groupId || '') === targetGroupId)
      .findIndex((field) => field.id === targetField.id);

    moveFieldToGroup(dragState.fieldIndex, targetGroupId, targetIndexWithinGroup);
    clearDragState();
  };

  const handleFieldContainerDrop = (targetGroupId) => {
    if (!dragState || dragState.type !== 'field') return;
    moveFieldToGroup(dragState.fieldIndex, targetGroupId || '');
    clearDragState();
  };

  const executeSave = useCallback(
    async (applyToExisting) => {
      if (!name.trim()) {
        alert('Nome do formulário é obrigatório');
        return;
      }

      setIsSavingForm(true);
      const schema = {
        groups,
        fields,
        settings: {
          showCompanyHeader,
          moduleIntegration,
        },
      };

      try {
        let successMessage = 'Formulário criado com sucesso';
        let response;
        if (editing) {
          response = await api.put(`/forms/${id}`, {
            name,
            schema,
            externalAccessEnabled,
            updateRegistrations: applyToExisting,
          });
          successMessage = 'Formulário atualizado com sucesso';
        } else {
          response = await api.post('/forms', { name, schema, externalAccessEnabled });
        }

        const saved = response?.data;
        if (saved) {
          setExternalAccessEnabled(Boolean(saved.externalAccessEnabled));
          setExternalAccessToken(saved.externalAccessToken || null);
        }

        if (editing && applyToExisting) {
          const updatedCount = Number(saved?.updatedRegistrations ?? 0);
          successMessage += updatedCount > 0
            ? ` • ${updatedCount} cadastro(s) sincronizado(s).`
            : ' • Nenhum cadastro existente necessitava de atualização.';
        }

        setToastMsg(successMessage);
        if (onSaved) onSaved();
        alert(successMessage);
        const signature = getCurrentSchemaSignature();
        initialSchemaRef.current = { serialized: signature };
        if (activateTab && returnToTabId) activateTab(returnToTabId);
        if (typeof closeTab === 'function') closeTab({ activateTabId: returnToTabId });
      } catch (err) {
        const msg = err.response?.data?.error || 'Erro ao salvar formulário';
        alert(msg);
      } finally {
        setIsSavingForm(false);
        setSchemaChangeModalOpen(false);
      }
    },
    [
      name,
      groups,
      fields,
      editing,
      id,
      externalAccessEnabled,
      showCompanyHeader,
      moduleIntegration,
      onSaved,
      activateTab,
      returnToTabId,
      closeTab,
      setExternalAccessEnabled,
      setExternalAccessToken,
      setToastMsg,
      getCurrentSchemaSignature,
    ]
  );

  const handleSave = async () => {
    if (isSavingForm) return;
    if (!name.trim()) {
      alert('Nome do formulário é obrigatório');
      return;
    }

    const schemaChanged =
      editing &&
      hasLinkedProcess &&
      getCurrentSchemaSignature() !== initialSchemaRef.current.serialized;

    if (schemaChanged) {
      setSchemaChangeModalOpen(true);
      return;
    }

    executeSave(false);
  };

  const handleConfirmUpdateExisting = useCallback(() => {
    setSchemaChangeModalOpen(false);
    executeSave(true);
  }, [executeSave]);

  const handleConfirmUpdateFuture = useCallback(() => {
    setSchemaChangeModalOpen(false);
    executeSave(false);
  }, [executeSave]);

  const handleCancelSchemaChange = useCallback(() => {
    setSchemaChangeModalOpen(false);
  }, []);

  const shareUrl = externalAccessToken
    ? typeof window !== 'undefined'
      ? `${window.location.origin}/formularios/${externalAccessToken}`
      : `/formularios/${externalAccessToken}`
    : '';

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopyFeedback('Link copiado!');
        return;
      }
      throw new Error('Clipboard API indisponível');
    } catch {
      try {
        if (typeof document === 'undefined') {
          throw new Error('Clipboard fallback indisponível');
        }
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopyFeedback('Link copiado!');
      } catch {
        setCopyFeedback('Copie manualmente o link exibido abaixo.');
      }
    }
  };

  const openTemplateModal = (anchorPoint = null) => {
    setTemplateModalAnchor(anchorPoint);
    setTemplateModalOpen(true);
  };

  const closeTemplateModal = () => {
    setTemplateModalOpen(false);
    setTemplateModalAnchor(null);
  };

  const openModuleIntegrationModal = (anchorPoint = null) => {
    setModuleIntegrationModalAnchor(anchorPoint);
    setModuleIntegrationModalOpen(true);
  };

  const closeModuleIntegrationModal = () => {
    setModuleIntegrationModalOpen(false);
    setModuleIntegrationModalAnchor(null);
    setPendingIntegrationTemplate(null);
  };

  const commitModuleIntegration = (template, mode) => {
    const next = updateModuleIntegration({ fields, groups, integration: moduleIntegration }, template, mode);
    captureHistorySnapshot();
    setGroups(next.groups.map(createGroup), { skipHistory: true });
    setFields(next.fields.map(createField), { skipHistory: true });
    setModuleIntegration(next.integration);
    if (!editing && !name.trim()) setName(template.name);
    closeModuleIntegrationModal();
    setToastMsg(mode === 'missing' ? 'Campos e colunas ausentes adicionados à integração.' : 'Integração recriada com os campos e grupos do modelo.');
  };

  const applyModuleIntegrationTemplate = (template) => {
    if (moduleIntegration || fields.some(field => field.moduleBinding?.moduleKey)) {
      setPendingIntegrationTemplate(template);
      return;
    }
    if ((fields.length || groups.length || name.trim()) && !window.confirm('Aplicar uma integração substituirá os campos e grupos atuais. Deseja continuar?')) return;
    commitModuleIntegration(template, 'replace');
  };

  const buildFormSnapshotForAi = useCallback(() => {
    if (!fields.length) {
      return 'Ainda não há campos adicionados.';
    }

    const groupDescriptions = groups.map((group) => {
      const groupFields = fields
        .filter((field) => field.groupId === group.id)
        .map((field) => `${field.label || field.id} (${getFieldTypeLabel(field.type)})`);
      const baseLabel = group.label?.trim() || 'Grupo sem título';
      const details = groupFields.length
        ? groupFields.join('; ')
        : 'Nenhum campo definido ainda para este grupo';
      return `Grupo "${baseLabel}": ${details}.`;
    });

    const ungroupedFields = fields
      .filter((field) => !field.groupId)
      .map((field) => `${field.label || field.id} (${getFieldTypeLabel(field.type)})`);

    if (ungroupedFields.length) {
      groupDescriptions.push(`Campos sem grupo: ${ungroupedFields.join('; ')}.`);
    }

    return groupDescriptions.join(' ');
  }, [fields, groups]);

  const openAiCoCreationModal = () => {
    aiDocument.reset();
    setAiCoCreationPrompt('');
    setAiCoCreationError('');
    setAiCoCreationSuggestion(null);
    setAiCoCreationSelectedFieldIds([]);
    setAiCoCreationSelectedGroupIds([]);
    setAiCoCreationLoading(false);
    setAiCoCreationOpen(true);
  };

  const closeAiCoCreationModal = () => {
    aiDocument.reset();
    setAiCoCreationSuggestion(null);
    setAiCoCreationOpen(false);
    setAiCoCreationLoading(false);
    setAiCoCreationError('');
    setAiCoCreationSelectedFieldIds([]);
    setAiCoCreationSelectedGroupIds([]);
  };

  const sanitizeSuggestedLabel = (label) => {
    const trimmed = (label || '')
      .replace(/[.:]+$/g, '')
      .replace(/^\s*(e|ou)\s+/i, '')
      .trim();

    if (!trimmed) return '';

    const instructionEchoes = [
      /evitando repetir/i,
      /já listad/i,
      /complementem o formulário/i,
      /^adicionados?$/i,
    ];

    if (instructionEchoes.some((pattern) => pattern.test(trimmed))) return '';

    return trimmed;
  };

  const handleGenerateAiCoCreation = async () => {
    const snapshot = buildFormSnapshotForAi();
    const userPrompt = aiCoCreationPrompt.trim();
    const promptParts = [
      name ? `Nome do formulário: ${name}.` : 'Formulário ainda sem nome.',
      'Estrutura atual registrada para a Nash IA analisar:',
      snapshot,
      'Sugira apenas novos campos e grupos que complementem o formulário, evitando repetir os já listados.',
      'Nunca devolva as instruções ou frases genéricas (por exemplo: "adicionados", "evitando repetir os já listados", "grupos que complementem o formulário").',
      aiDocument.file
        ? 'Reproduza todos os campos e grupos do modelo anexado, mantendo os rótulos completos, tipos, opções, tamanhos e colunas de tabelas. Preserve os campos atuais e não duplique os já existentes. Associe os novos campos aos grupos corretos, inclusive grupos já existentes.'
        : 'Use nomes curtos (até 6 palavras) para os campos e grupos e responda apenas com itens que façam sentido para o processo descrito.',
    ];

    if (userPrompt) {
      promptParts.push(`Instruções adicionais do usuário: ${userPrompt}`);
    }

    const operation = aiDocument.start();
    setAiCoCreationLoading(true);
    setAiCoCreationError('');
    setAiCoCreationSuggestion(null);

    try {
      const request = buildFormAiRequest(promptParts.join(' '), aiDocument.file);
      const response = await api.post(request.url, request.data, { signal: operation.signal });
      if (!aiDocument.isCurrent(operation)) return;
      const generated = response.data?.form;
      const normalized = normalizeFormAiSuggestion(
        generated?.schema || {}, groupsRef.current, fieldsRef.current,
        aiDocument.file ? undefined : sanitizeSuggestedLabel,
      );
      const newGroups = normalized.groups.map((group) => createGroup(group));
      const newFields = normalized.fields.map((field) => createField(field));

      if (!newFields.length && !newGroups.length) {
        setAiCoCreationError('A Nash IA não encontrou novos campos para sugerir com a estrutura atual.');
        setAiCoCreationSuggestion(null);
        return;
      }

      setAiCoCreationSuggestion({
        summary: generated?.summary || '',
        suggestions: Array.isArray(generated?.suggestions) ? generated.suggestions : [],
        keywords: Array.isArray(generated?.keywords) ? generated.keywords : [],
        groups: newGroups,
        fields: newFields,
      });
      setAiCoCreationSelectedGroupIds(newGroups.map((group) => group.id));
      setAiCoCreationSelectedFieldIds(newFields.map((field) => field.id));
    } catch (error) {
      if (!aiDocument.isCurrent(operation)) return;
      const message = error?.response?.data?.error || error?.message || 'Não foi possível gerar sugestões com a Nash IA.';
      setAiCoCreationError(message);
    } finally {
      if (aiDocument.isCurrent(operation)) setAiCoCreationLoading(false);
    }
  };

  const applyAiCoCreationSuggestion = (selection = {}) => {
    if (!aiCoCreationSuggestion) return;
    const { groups: suggestedGroups = [], fields: suggestedFields = [] } = aiCoCreationSuggestion;
    const selectedFieldIds = Array.isArray(selection.fieldIds)
      ? selection.fieldIds
      : aiCoCreationSelectedFieldIds;
    const selectedGroupIds = Array.isArray(selection.groupIds)
      ? selection.groupIds
      : aiCoCreationSelectedGroupIds;

    const selectedFields = suggestedFields.filter((field) => selectedFieldIds.includes(field.id));
    const autoRequiredGroupIds = selectedFields
      .map((field) => field.groupId)
      .filter((groupId) => groupId && suggestedGroups.some((group) => group.id === groupId));
    const finalGroupIds = new Set([...selectedGroupIds, ...autoRequiredGroupIds]);
    const selectedGroups = suggestedGroups.filter((group) => finalGroupIds.has(group.id));

    if (!selectedGroups.length && !selectedFields.length) {
      setAiCoCreationError('Selecione ao menos um grupo ou campo para adicionar.');
      return;
    }

    if (selectedGroups.length) {
      setGroups((prev) => [...prev, ...selectedGroups]);
    }
    if (selectedFields.length) {
      setFields((prev) => [...prev, ...selectedFields]);
    }

    const remainingGroups = suggestedGroups.filter((group) => !finalGroupIds.has(group.id));
    const remainingFields = suggestedFields.filter((field) => !selectedFieldIds.includes(field.id));
    if (!remainingGroups.length && !remainingFields.length) {
      setAiCoCreationSuggestion(null);
      setAiCoCreationSelectedFieldIds([]);
      setAiCoCreationSelectedGroupIds([]);
    } else {
      setAiCoCreationSuggestion((prev) =>
        prev
          ? {
              ...prev,
              groups: remainingGroups,
              fields: remainingFields,
            }
          : prev
      );
      setAiCoCreationSelectedFieldIds(remainingFields.map((field) => field.id));
      setAiCoCreationSelectedGroupIds(remainingGroups.map((group) => group.id));
    }
    const totalAdded = selectedGroups.length + selectedFields.length;
    const groupText = selectedGroups.length ? `${selectedGroups.length} grupo(s)` : '';
    const fieldText = selectedFields.length ? `${selectedFields.length} campo(s)` : '';
    const summary = [groupText, fieldText].filter(Boolean).join(' e ');
    setToastMsg(`${summary || `${totalAdded} item(ns)`} adicionado(s) ao formulário.`);
    setAiCoCreationError('');
  };

  const toggleAiSuggestedField = (fieldId) => {
    if (!fieldId) return;
    setAiCoCreationSelectedFieldIds((prev) =>
      prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : [...prev, fieldId]
    );
  };

  const toggleAiSuggestedGroup = (groupId) => {
    if (!groupId) return;
    setAiCoCreationSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const selectAllAiSuggestions = () => {
    if (!aiCoCreationSuggestion) return;
    setAiCoCreationSelectedFieldIds(aiCoCreationSuggestion.fields.map((field) => field.id));
    setAiCoCreationSelectedGroupIds(aiCoCreationSuggestion.groups.map((group) => group.id));
  };

  const clearAiSuggestionSelection = () => {
    setAiCoCreationSelectedFieldIds([]);
    setAiCoCreationSelectedGroupIds([]);
  };

  const openPreview = () => {
    if (openTab) {
      const tabId = `form-preview-${id || 'new'}`;
      openTab(tabId, 'Prévia', FormPreview, { name, groups, fields });
    }
  };

  const formSnapshotForAi = buildFormSnapshotForAi();

  return (
    <div className="forms-workspace form-builder-page space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">
          {editing ? 'Editar Formulário' : 'Novo Formulário'}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="flex items-center rounded border border-indigo-200 bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100"
            onClick={(event) => openModuleIntegrationModal(getAnchorPointFromEvent(event))}
          >
            <FaPlug className="mr-2" /> Integração de módulos.
          </button>
          <button
            type="button"
            className="flex items-center rounded border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition hover:bg-blue-100"
            onClick={(event) => openTemplateModal(getAnchorPointFromEvent(event))}
          >
            <FaMagic className="mr-2" /> Modelos
          </button>
          <button
            className="flex items-center rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
            onClick={(event) => openNewGroup(getAnchorPointFromEvent(event))}
            type="button"
          >
            <FaPlus className="mr-2" /> Novo grupo
          </button>
          <button
            type="button"
            className="flex items-center rounded border border-blue-200 bg-white px-3 py-1 text-sm font-medium text-blue-600 transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={undo}
            disabled={!canUndo}
            title="Ctrl+Z"
          >
            <FaUndo className="mr-2" /> Desfazer
          </button>
          {fieldClipboard && (
            <button
              type="button"
              className="flex items-center rounded border border-blue-200 bg-white px-3 py-1 text-sm font-medium text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"
              onClick={() => handlePasteField('')}
            >
              <FaPaste className="mr-2" /> Colar campo
            </button>
          )}
          <button
            type="button"
            className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
            onClick={openPreview}
          >
            Prévia
          </button>
          <button
            className="rounded bg-green-600 px-3 py-1 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={handleSave}
            disabled={isSavingForm}
          >
            {isSavingForm ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
      {fieldClipboard && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50/70 px-4 py-3 text-sm text-blue-700">
          <FaCopy className="text-base" />
          <div className="min-w-[200px] flex-1">
            <p className="font-medium">
              Campo copiado: {fieldClipboard.label || fieldClipboard.id || 'Sem título'}
            </p>
            {fieldClipboard.description && (
              <p className="text-xs text-blue-600/80">{fieldClipboard.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={clearFieldClipboard}
            className="rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600 transition hover:border-blue-300 hover:bg-white"
          >
            Limpar
          </button>
        </div>
      )}
      <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-inner">
              <FaGlobe />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-blue-900">Compartilhamento externo</h2>
            </div>
          </div>
          <label className="inline-flex items-center gap-3 rounded-full bg-white px-4 py-2 text-sm font-medium text-blue-900 shadow">
            <span>{externalAccessEnabled ? 'Ativado' : 'Desativado'}</span>
            <span className="relative inline-flex h-6 w-11 items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={externalAccessEnabled}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setExternalAccessEnabled(enabled);
                  if (enabled) {
                    setFields((prev) => prev.filter((field) => field.type !== 'linkedField'));
                    if (fieldForm.type === 'linkedField') {
                      setFieldForm(createField({ groupId: fieldForm.groupId || '', type: 'text' }));
                    }
                  }
                  if (!enabled) setExternalAccessToken(null);
                  setCopyFeedback('');
                }}
              />
              <span className="absolute inset-0 rounded-full bg-slate-300 transition peer-checked:bg-blue-600" />
              <span className="absolute left-1 h-4 w-4 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
            </span>
          </label>
        </div>
        {externalAccessEnabled ? (
          <div className="mt-4 rounded-xl border border-blue-200 bg-white/80 p-4 text-sm text-blue-900">
            <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2">
              <div>
                <p className="font-medium text-blue-900">Exibir dados da empresa no cabeçalho</p>
              </div>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-blue-900">
                <input
                  type="checkbox"
                  checked={showCompanyHeader}
                  onChange={(event) => setShowCompanyHeader(event.target.checked)}
                  className="h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                />
                Mostrar
              </label>
            </div>
            {externalAccessToken ? (
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-2 break-all">
                  <FaLink />
                  <span>{shareUrl}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white shadow hover:bg-blue-700"
                  >
                    <FaCopy /> Copiar link
                  </button>
                  {copyFeedback && <span className="text-xs text-blue-800">{copyFeedback}</span>}
                </div>
              </div>
            ) : (
              <p className="text-sm text-blue-800">
                Salve o formulário para gerar o link público automaticamente.
              </p>
            )}
          </div>
        ) : (
          <p className="mt-4 text-sm text-blue-900/70">
            Ao ativar, um link seguro será criado para distribuir aos clientes externos.
          </p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700" htmlFor="form-name">
          Nome do formulário
        </label>
        <input
          id="form-name"
          className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder="Ex.: Cadastro de clientes"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h2 className="flex items-center text-base font-semibold text-blue-800">
            <FaMagic className="mr-2" /> Modelos prontos
          </h2>
          <button
            type="button"
            className="mt-3 rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
            onClick={(event) => openTemplateModal(getAnchorPointFromEvent(event))}
          >
            Explorar modelos
          </button>
          <button
            type="button"
            className="ml-2 mt-3 inline-flex items-center gap-2 rounded border border-indigo-200 bg-white px-3 py-1 text-sm font-medium text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50"
            onClick={(event) => openModuleIntegrationModal(getAnchorPointFromEvent(event))}
          >
            <FaPlug /> Integração de módulos.
          </button>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="text-base font-semibold text-slate-900">Adicionar campos rapidamente</h2>
          {fieldClipboard && (
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
              onClick={() => handlePasteField(defaultGroupId)}
            >
              <FaPaste className="text-xs" /> Colar campo copiado
            </button>
          )}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {fieldTypeOptions.map(([type, meta]) => {
              const Icon = meta.icon;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={(event) =>
                    openNewFieldByType(type, defaultGroupId, getAnchorPointFromEvent(event))
                  }
                  className="flex items-start rounded border border-slate-200 bg-slate-50 p-3 text-left transition hover:border-blue-400 hover:bg-blue-50"
                >
                  <Icon className="mt-0.5 h-5 w-5 text-blue-500" />
                  <span className="ml-3">
                    <span className="block text-sm font-semibold text-slate-900">{meta.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-purple-200 bg-purple-50 p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center text-base font-semibold text-purple-800">
              <FaRobot className="mr-2" /> Co-criar com a Nash IA
            </h2>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-purple-700">
              {fields.length} campo(s) • {groups.length} grupo(s)
            </span>
            <button
              type="button"
              onClick={openAiCoCreationModal}
              className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-purple-700"
            >
              <FaMagic className="text-xs" /> Pedir ajuda da Nash IA
            </button>
          </div>
        </div>
      </div>
      {groups.map((g, gIdx) => (
        <div
          key={g.id}
          className={`mb-6 rounded-lg border bg-white p-4 shadow-sm transition ${dragState?.type === 'group' && dragState.groupIndex === gIdx ? 'border-blue-400 opacity-70' : 'border-slate-200'}`}
          draggable
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = 'move';
            handleGroupDragStart(gIdx);
          }}
          onDragOver={(event) => {
            if (dragState?.type === 'group') event.preventDefault();
          }}
          onDrop={(event) => {
            event.preventDefault();
            handleGroupDrop(gIdx);
          }}
          onDragEnd={clearDragState}
        >
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="flex flex-1 flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nome do grupo</label>
              <input
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={g.label}
                onChange={(e) => updateGroupLabel(g.id, e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <span
                className="cursor-grab rounded border border-slate-200 p-2 text-slate-400 active:cursor-grabbing"
                title="Arraste para reordenar o grupo"
              >
                <FaGripVertical />
              </span>
              <button
                type="button"
                onClick={() => moveGroupUp(gIdx)}
                disabled={gIdx === 0}
                className="rounded border border-slate-200 p-2 text-slate-500 transition hover:border-blue-300 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowUp />
              </button>
              <button
                type="button"
                onClick={() => moveGroupDown(gIdx)}
                disabled={gIdx === groups.length - 1}
                className="rounded border border-slate-200 p-2 text-slate-500 transition hover:border-blue-300 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowDown />
              </button>
            </div>
            <button
              type="button"
              onClick={(event) => openEditGroup(gIdx, getAnchorPointFromEvent(event))}
              className="flex items-center rounded border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-300 hover:text-blue-600"
            >
              <FaCog className="mr-2" /> Configurar
            </button>
            <button
              type="button"
              onClick={(event) => openNewField(g.id, 'text', getAnchorPointFromEvent(event))}
              className="flex items-center rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              <FaPlus className="mr-2" /> Adicionar campo
            </button>
            {fieldClipboard && (
              <button
                type="button"
                onClick={() => handlePasteField(g.id)}
                className="flex items-center rounded border border-blue-200 px-3 py-2 text-sm font-medium text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"
              >
                <FaPaste className="mr-2" /> Colar campo
              </button>
            )}
            <button
              type="button"
              onClick={() => removeGroup(g.id)}
              className="rounded border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
            >
              <FaTrash className="mr-1" /> Remover
            </button>
          </div>
          <div
            className={`grid grid-cols-12 gap-2 rounded-lg border border-dashed p-2 transition ${dragState?.type === 'field' && (dragState.sourceGroupId || '') !== g.id ? 'border-blue-300 bg-blue-50/50' : 'border-transparent'}`}
            onDragOver={(event) => {
              if (dragState?.type === 'field') event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              handleFieldContainerDrop(g.id);
            }}
          >
            {fields
              .filter((f) => f.groupId === g.id)
              .map((f, idx) => {
                const fIndex = fields.indexOf(f);
                const typeInfo = FIELD_TYPE_METADATA[f.type] || {};
                const typeLabel = getFieldTypeLabel(f.type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div
                    key={`${g.id}-${idx}`}
                    className={`form-builder-field-card flex cursor-pointer flex-col rounded border bg-white p-3 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 ${dragState?.type === 'field' && dragState.fieldIndex === fIndex ? 'border-blue-400 opacity-60' : 'border-slate-200'}`}
                    draggable
                    onDragStart={(event) => {
                      event.stopPropagation();
                      event.dataTransfer.effectAllowed = 'move';
                      handleFieldDragStart(fIndex);
                    }}
                    onDragOver={(event) => {
                      if (dragState?.type === 'field') event.preventDefault();
                      event.stopPropagation();
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleFieldDrop(fIndex);
                    }}
                    onDragEnd={clearDragState}
                    style={{ gridColumn: `span ${f.size} / span ${f.size}` }}
                    onClick={(event) => openEditField(fIndex, getAnchorPointFromEvent(event))}
                  >
                    <div className="flex items-start gap-2">
                      {TypeIcon && (
                        <TypeIcon className="mt-1 h-4 w-4 flex-shrink-0 text-blue-500" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <label className="flex-1 font-medium text-slate-900">{f.label}</label>
                          <div className="flex gap-1">
                            <span
                              className="cursor-grab rounded border border-transparent p-1 text-slate-400 active:cursor-grabbing"
                              title="Arraste para reordenar o campo"
                            >
                              <FaGripVertical className="text-xs" />
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleCopyField(fIndex); }}
                              className="rounded border border-transparent p-1 text-slate-500 transition hover:border-slate-300 hover:bg-white"
                              aria-label="Copiar campo"
                            >
                              <FaCopy className="text-xs" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); moveFieldUp(fIndex); }}
                              className="rounded border border-transparent p-1 text-slate-500 transition hover:border-slate-300 hover:bg-white"
                            >
                              <FaArrowLeft className="text-xs" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); moveFieldDown(fIndex); }}
                              className="rounded border border-transparent p-1 text-slate-500 transition hover:border-slate-300 hover:bg-white"
                            >
                              <FaArrowRight className="text-xs" />
                            </button>
                          </div>
                        </div>
                        {f.description && (
                          <p className="mt-1 text-xs text-slate-500">{f.description}</p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                            {typeLabel}
                          </span>
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                            Largura: {f.size}/12
                          </span>
                          {f.type === 'file' && f.fileNameTemplate && (
                            <span className="rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                              Nome automático
                            </span>
                          )}
                          {f.required && (
                            <span className="rounded bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">
                              Obrigatório
                            </span>
                          )}
                          {f.allowAiAnswer && (
                            <span className="rounded bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                              IA liberada
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      {f.type === 'list' ? (
                        <div className="flex flex-wrap gap-1">
                          {(f.academicOptions || f.options || []).length > 0 ? (
                            [...(f.academicOptions || f.options || []), ...(f.allowOtherOption ? [f.otherOptionLabel || 'Outra opção'] : [])].map((o, i) => (
                              <span
                                key={`${f.id}-option-${i}`}
                                className="form-builder-option-chip rounded-full bg-blue-100 px-2 py-0.5 text-[11px] text-blue-700"
                              >
                                {typeof o === 'object' ? o.label : o}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-slate-500">Configure as opções deste campo.</span>
                          )}
                        </div>
                      ) : f.type === 'textarea' ? (
                        <textarea
                          name={f.id}
                          rows={3}
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : f.type === 'document' ? (
                        <div className="rounded border border-indigo-100 bg-indigo-50 p-2 text-xs text-indigo-700">PDF: {f.documentTemplate?.title || 'Documento sem título'}</div>
                      ) : f.type === 'file' ? (
                        <input type="file" disabled className="w-full cursor-not-allowed rounded border border-dashed border-slate-300 px-2 py-1 text-sm text-slate-500" />
                      ) : f.type === 'table' ? (
                        <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                          {(f.tableColumns || []).length > 0
                            ? `${f.tableColumns.length} coluna(s) configurada(s)`
                            : 'Configure as colunas da tabela.'}
                        </div>
                      ) : f.type === 'decimal' ? (
                        <div className="space-y-1">
                          <DecimalInput
                            decimalPlaces={f.decimalPlaces || 0}
                            readOnly
                            className="w-full rounded border border-slate-300 px-2 py-1"
                          />
                          <p className="text-[11px] text-slate-500">Casas decimais: {f.decimalPlaces || 0}</p>
                        </div>
                      ) : f.type === 'phone' ? (
                        <MaskedInput
                          mask="phone"
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : (f.type === 'cpf' || f.mask === 'cpf') ? (
                        <MaskedInput
                          mask="cpf"
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : f.type === 'cnpj' ? (
                        <MaskedInput
                          mask="cnpj"
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : f.type === 'cep' ? (
                        <MaskedInput
                          mask="cep"
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : f.type === 'email' ? (
                        <input
                          type="email"
                          name={f.id}
                          className="w-full rounded border border-slate-300 px-2 py-1"
                          readOnly
                          inputMode="email"
                          autoComplete="email"
                          pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                        />
                      ) : f.type === 'date' ? (
                        <input
                          type="date"
                          name={f.id}
                          className="w-full rounded border border-slate-300 px-2 py-1"
                          readOnly
                          onFocus={(e) => e.target.showPicker?.()}
                        />
                      ) : (
                        <input
                          type={f.type}
                          name={f.id}
                          className="w-full rounded border border-slate-300 px-2 py-1"
                          readOnly
                        />
                      )}
                      {f.visibleWhen && (
                        <p className="mt-2 text-[11px] text-slate-500">
                          Visível quando: <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px]">{f.visibleWhen}</code>
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ))}
      {fields.filter((f) => !f.groupId).length > 0 && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          {(groups.length > 0 || fieldClipboard) && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              {groups.length > 0 && (
                <h3 className="text-lg font-semibold text-slate-900">Campos sem grupo</h3>
              )}
              {fieldClipboard && (
                <button
                  type="button"
                  onClick={() => handlePasteField('')}
                  className="inline-flex items-center gap-2 rounded border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:border-blue-300 hover:bg-blue-50"
                >
                  <FaPaste className="text-xs" /> Colar campo
                </button>
              )}
            </div>
          )}
          <div
            className={`grid grid-cols-12 gap-2 rounded-lg border border-dashed p-2 transition ${dragState?.type === 'field' && (dragState.sourceGroupId || '') !== '' ? 'border-blue-300 bg-blue-50/50' : 'border-transparent'}`}
            onDragOver={(event) => {
              if (dragState?.type === 'field') event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              handleFieldContainerDrop('');
            }}
          >
            {fields
              .filter((f) => !f.groupId)
              .map((f, idx) => {
                const fIndex = fields.indexOf(f);
                const typeInfo = FIELD_TYPE_METADATA[f.type] || {};
                const typeLabel = getFieldTypeLabel(f.type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div
                    key={`nogroup-${idx}`}
                    className={`form-builder-field-card flex cursor-pointer flex-col rounded border bg-white p-3 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 ${dragState?.type === 'field' && dragState.fieldIndex === fIndex ? 'border-blue-400 opacity-60' : 'border-slate-200'}`}
                    draggable
                    onDragStart={(event) => {
                      event.stopPropagation();
                      event.dataTransfer.effectAllowed = 'move';
                      handleFieldDragStart(fIndex);
                    }}
                    onDragOver={(event) => {
                      if (dragState?.type === 'field') event.preventDefault();
                      event.stopPropagation();
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleFieldDrop(fIndex);
                    }}
                    onDragEnd={clearDragState}
                    style={{ gridColumn: `span ${f.size} / span ${f.size}` }}
                    onClick={() => openEditField(fIndex)}
                  >
                    <div className="flex items-start gap-2">
                      {TypeIcon && (
                        <TypeIcon className="mt-1 h-4 w-4 flex-shrink-0 text-blue-500" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <label className="flex-1 font-medium text-slate-900">{f.label}</label>
                          <div className="flex gap-1">
                            <span
                              className="cursor-grab rounded border border-transparent p-1 text-slate-400 active:cursor-grabbing"
                              title="Arraste para reordenar o campo"
                            >
                              <FaGripVertical className="text-xs" />
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleCopyField(fIndex); }}
                              className="rounded border border-transparent p-1 text-slate-500 transition hover:border-slate-300 hover:bg-white"
                              aria-label="Copiar campo"
                            >
                              <FaCopy className="text-xs" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); moveFieldUp(fIndex); }}
                              className="rounded border border-transparent p-1 text-slate-500 transition hover:border-slate-300 hover:bg-white"
                            >
                              <FaArrowLeft className="text-xs" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); moveFieldDown(fIndex); }}
                              className="rounded border border-transparent p-1 text-slate-500 transition hover:border-slate-300 hover:bg-white"
                            >
                              <FaArrowRight className="text-xs" />
                            </button>
                          </div>
                        </div>
                        {f.description && (
                          <p className="mt-1 text-xs text-slate-500">{f.description}</p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                            {typeLabel}
                          </span>
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                            Largura: {f.size}/12
                          </span>
                          {f.type === 'file' && f.fileNameTemplate && (
                            <span className="rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                              Nome automático
                            </span>
                          )}
                          {f.required && (
                            <span className="rounded bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">
                              Obrigatório
                            </span>
                          )}
                          {f.allowAiAnswer && (
                            <span className="rounded bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                              IA liberada
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      {f.type === 'list' ? (
                        <div className="flex flex-wrap gap-1">
                          {(f.academicOptions || f.options || []).length > 0 ? (
                            [...(f.academicOptions || f.options || []), ...(f.allowOtherOption ? [f.otherOptionLabel || 'Outra opção'] : [])].map((o, i) => (
                              <span
                                key={`${f.id}-nogroup-option-${i}`}
                                className="form-builder-option-chip rounded-full bg-blue-100 px-2 py-0.5 text-[11px] text-blue-700"
                              >
                                {typeof o === 'object' ? o.label : o}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-slate-500">Configure as opções deste campo.</span>
                          )}
                        </div>
                      ) : f.type === 'document' ? (
                        <div className="rounded border border-indigo-100 bg-indigo-50 p-2 text-xs text-indigo-700">PDF: {f.documentTemplate?.title || 'Documento sem título'}</div>
                      ) : f.type === 'file' ? (
                        <input type="file" disabled className="w-full cursor-not-allowed rounded border border-dashed border-slate-300 px-2 py-1 text-sm text-slate-500" />
                      ) : f.type === 'table' ? (
                        <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                          {(f.tableColumns || []).length > 0
                            ? `${f.tableColumns.length} coluna(s) configurada(s)`
                            : 'Configure as colunas da tabela.'}
                        </div>
                      ) : f.type === 'decimal' ? (
                        <div className="space-y-1">
                          <DecimalInput
                            decimalPlaces={f.decimalPlaces || 0}
                            readOnly
                            className="w-full rounded border border-slate-300 px-2 py-1"
                          />
                          <p className="text-[11px] text-slate-500">Casas decimais: {f.decimalPlaces || 0}</p>
                        </div>
                      ) : f.type === 'phone' ? (
                        <MaskedInput
                          mask="phone"
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : (f.type === 'cpf' || f.mask === 'cpf') ? (
                        <MaskedInput
                          mask="cpf"
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : f.type === 'cnpj' ? (
                        <MaskedInput
                          mask="cnpj"
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : f.type === 'cep' ? (
                        <MaskedInput
                          mask="cep"
                          readOnly
                          className="w-full rounded border border-slate-300 px-2 py-1"
                        />
                      ) : f.type === 'email' ? (
                        <input
                          type="email"
                          name={f.id}
                          className="w-full rounded border border-slate-300 px-2 py-1"
                          readOnly
                          inputMode="email"
                          autoComplete="email"
                          pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                        />
                      ) : f.type === 'date' ? (
                        <input
                          type="date"
                          name={f.id}
                          className="w-full rounded border border-slate-300 px-2 py-1"
                          readOnly
                          onFocus={(e) => e.target.showPicker?.()}
                        />
                      ) : (
                        <input
                          type={f.type}
                          name={f.id}
                          className="w-full rounded border border-slate-300 px-2 py-1"
                          readOnly
                        />
                      )}
                      {f.visibleWhen && (
                        <p className="mt-2 text-[11px] text-slate-500">
                          Visível quando: <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px]">{f.visibleWhen}</code>
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}



      <Modal open={aiCoCreationOpen} onClose={closeAiCoCreationModal}>
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Co-criar formulário com a Nash IA</h2>
            <p className="mt-1 text-sm text-slate-600">
              Anexe um modelo para reproduzir seus campos e grupos ou peça novas sugestões. Revise a proposta antes de adicionar ao formulário.
            </p>
            <p className="text-xs uppercase tracking-[0.25em] text-purple-700">
              {fields.length} campo(s) e {groups.length} grupo(s) enviados como contexto
            </p>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700" htmlFor="ai-cocreate-prompt">
              Instruções adicionais (opcional)
            </label>
            <textarea
              id="ai-cocreate-prompt"
              value={aiCoCreationPrompt}
              onChange={(event) => setAiCoCreationPrompt(event.target.value)}
              rows={3}
              placeholder="Ex.: precisamos de campos de aprovação e anexos de comprovantes."
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
            />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              <FaRobot className="text-slate-400" /> Estrutura que a Nash IA vai ler
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{formSnapshotForAi}</p>
          </div>
          <FormAiDocumentAttachment file={aiDocument.file} loading={aiCoCreationLoading} onChange={(file) => {
            const error = aiDocument.choose(file);
            setAiCoCreationLoading(false);
            setAiCoCreationSuggestion(null);
            setAiCoCreationSelectedFieldIds([]);
            setAiCoCreationSelectedGroupIds([]);
            setAiCoCreationError(error);
          }} />
          {aiCoCreationError && <p role="alert" className="text-sm text-red-600">{aiCoCreationError}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeAiCoCreationModal}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={handleGenerateAiCoCreation}
              disabled={aiCoCreationLoading}
              className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaMagic className="text-xs" /> {aiCoCreationLoading ? 'Gerando...' : 'Gerar sugestões'}
            </button>
          </div>
          {aiCoCreationSuggestion && (
            <div className="space-y-3 rounded-2xl border border-purple-200 bg-white/90 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-purple-800">Sugestões da Nash IA</h3>
                  {aiCoCreationSuggestion.summary && (
                    <p className="text-sm text-slate-700">{aiCoCreationSuggestion.summary}</p>
                  )}
                </div>
                <div className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-purple-700">
                  {aiCoCreationSuggestion.fields.length} campo(s) novo(s)
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllAiSuggestions}
                  className="rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700 transition hover:bg-purple-100"
                >
                  Selecionar tudo
                </button>
                <button
                  type="button"
                  onClick={clearAiSuggestionSelection}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  Limpar seleção
                </button>
                <button
                  type="button"
                  onClick={() => applyAiCoCreationSuggestion()}
                  className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-3 py-1 text-xs font-semibold text-white shadow transition hover:bg-purple-700"
                >
                  <FaPlus className="text-[10px]" /> Adicionar selecionados
                </button>
              </div>
              {aiCoCreationSuggestion.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {aiCoCreationSuggestion.keywords.map((keyword) => (
                    <span key={keyword} className="rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700">
                      {keyword}
                    </span>
                  ))}
                </div>
              )}
              {aiCoCreationSuggestion.groups.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Novos grupos</span>
                  <div className="flex flex-wrap gap-2">
                    {aiCoCreationSuggestion.groups.map((group) => (
                      <label key={group.id} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                        <input
                          type="checkbox"
                          checked={aiCoCreationSelectedGroupIds.includes(group.id)}
                          onChange={() => toggleAiSuggestedGroup(group.id)}
                        />
                        {group.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid gap-3 md:grid-cols-2">
                {aiCoCreationSuggestion.fields.map((field) => {
                  const typeLabel = getFieldTypeLabel(field.type);
                  const groupLabel =
                    field.groupId &&
                    (
                      groups.find((group) => group.id === field.groupId)?.label ||
                      aiCoCreationSuggestion.groups.find((group) => group.id === field.groupId)?.label
                    );
                  return (
                    <div key={field.id} className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 text-sm text-slate-700">
                      <label className="mb-2 inline-flex items-center gap-2 text-xs font-medium text-slate-600">
                        <input
                          type="checkbox"
                          checked={aiCoCreationSelectedFieldIds.includes(field.id)}
                          onChange={() => toggleAiSuggestedField(field.id)}
                        />
                        Selecionar campo
                      </label>
                      <p className="text-base font-semibold text-slate-900">{field.label}</p>
                      <p className="text-xs text-slate-500">Tipo: {typeLabel}</p>
                      {groupLabel && (
                        <p className="text-xs text-slate-500">Grupo: {groupLabel}</p>
                      )}
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() =>
                            applyAiCoCreationSuggestion({
                              fieldIds: [field.id],
                              groupIds: field.groupId ? [field.groupId] : [],
                            })
                          }
                          className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-white px-3 py-1 text-xs font-semibold text-purple-700 transition hover:bg-purple-50"
                        >
                          <FaPlus className="text-[10px]" /> Adicionar este campo
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {aiCoCreationSuggestion.suggestions.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                  {aiCoCreationSuggestion.suggestions.map((tip, index) => (
                    <li key={`tip-${index}`}>{tip}</li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => applyAiCoCreationSuggestion()}
                  className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-purple-700"
                >
                  <FaPlus className="text-xs" /> Adicionar selecionados
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal open={schemaChangeModalOpen} onClose={handleCancelSchemaChange}>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Aplicar alterações aos cadastros existentes?</h2>
          <p className="mt-1 text-sm text-slate-600">
            O formulário está vinculado ao processo <strong>{linkedProcess?.name || 'selecionado'}</strong>. Deseja atualizar também os cadastros já realizados na Central de Cadastros?
          </p>
          <p className="mt-3 text-sm font-medium text-red-600">
            Atenção: atualizar os cadastros existentes é uma ação irreversível.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleConfirmUpdateFuture}
            className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 sm:w-auto"
          >
            Aplicar somente para novos cadastros
          </button>
          <button
            type="button"
            onClick={handleConfirmUpdateExisting}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 sm:w-auto"
          >
            Atualizar cadastros existentes
          </button>
          <button
            type="button"
            onClick={handleCancelSchemaChange}
            className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 sm:w-auto"
          >
            Cancelar
          </button>
        </div>
      </Modal>

      <Modal
        open={modalOpen}
        onClose={closeFieldModal}
        anchor={modalAnchor}
        size={fieldForm.type === 'document' && documentEditorExpanded ? 'max-w-6xl' : 'max-w-lg'}
        panelClassName={fieldForm.type === 'document' && documentEditorExpanded ? 'min-h-[80vh]' : ''}
      >
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{editingIndex !== null ? 'Editar Campo' : 'Novo Campo'}</h2>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700" htmlFor="field-label">Rótulo</label>
              <input
                id="field-label"
                name="label"
                placeholder="Ex.: Número do pedido"
                value={fieldForm.label}
                onChange={handleFieldChange}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700" htmlFor="field-description">Descrição (opcional)</label>
              <textarea
                id="field-description"
                name="description"
                placeholder="Explique brevemente a finalidade do campo"
                value={fieldForm.description}
                onChange={handleFieldChange}
                rows={2}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor="field-id">ID</label>
              <input
                id="field-id"
                name="id"
                placeholder="ex.: numeropedido"
                value={fieldForm.id}
                onChange={handleFieldChange}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                disabled={editingIndex !== null && hasLinkedProcess}
              />
              {hasLinkedProcess ? (
                <p className="mt-1 text-xs text-slate-500">
                  Como o formulário está vinculado a um processo, o ID dos campos existentes não pode ser alterado.
                  {linkedProcess?.name ? ` Processo: "${linkedProcess.name}".` : ''}
                </p>
              ) : (
                <p className="mt-1 text-xs text-slate-500">
                  Você pode ajustar o ID dos campos enquanto o formulário não estiver vinculado a um processo.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor="field-type">Tipo</label>
              <select
                id="field-type"
                name="type"
                value={fieldForm.type}
                onChange={handleFieldChange}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="text">Texto</option>
                <option value="textarea">Texto longo</option>
                <option value="email">E-mail</option>
                <option value="phone">Telefone</option>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="cep">CEP</option>
                <option value="date">Data</option>
                <option value="time">Hora</option>
                <option value="number">Número inteiro</option>
                <option value="decimal">Número decimal</option>
                <option value="list">Lista</option>
                <option value="dynamic-select">Seleção colaborativa</option>
                <option value="file">Anexo</option>
                <option value="signature-attachment">Anexo para Assinatura Eletrônica</option>
                <option value="table">Tabela</option>
                <option value="linkedField" disabled={externalAccessEnabled}>
                  Campo vinculado {externalAccessEnabled ? '(indisponível no compartilhamento externo)' : ''}
                </option>
                <option value="document">Gerar Documento</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor="field-group">Grupo</label>
              <select
                id="field-group"
                name="groupId"
                value={fieldForm.groupId}
                onChange={handleFieldChange}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Sem grupo</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700" htmlFor="field-size">Largura (1-12)</label>
              <IntegerInput
                id="field-size"
                name="size"
                min="1"
                max="12"
                value={fieldForm.size}
                onChange={handleFieldChange}
                className={`mt-1 w-full rounded border px-3 py-2 ${isFieldSizeInvalid ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-slate-300'}`}
              />
              {isFieldSizeInvalid && (
                <p className="mt-1 text-xs text-red-600">Informe uma largura válida entre 1 e 12.</p>
              )}
            </div>
            <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                name="required"
                checked={fieldForm.required}
                onChange={handleFieldChange}
              />
              Obrigatório
            </label>
            <label className="flex items-center gap-2 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800">
              <input
                type="checkbox"
                name="allowAiAnswer"
                checked={Boolean(fieldForm.allowAiAnswer)}
                onChange={handleFieldChange}
              />
              Permitir resposta com IA?
            </label>
            <div className="md:col-span-2 space-y-2">
              <label className="block text-sm font-medium text-slate-700" htmlFor="field-visible">Visível quando (expressão)</label>
              <textarea
                id="field-visible"
                name="visibleWhen"
                value={fieldForm.visibleWhen}
                onChange={handleFieldChange}
                placeholder="Ex.: status === 'aprovado'"
                rows={Math.max(2, fieldForm.visibleWhen.split('\n').length)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <div className="space-y-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Mostrar este campo somente se...</p>
                    <p className="mt-1 text-xs text-slate-600">Crie a condição escolhendo um campo, a comparação e o valor esperado.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFieldForm((prev) => ({ ...prev, visibleWhen: '' }))}
                    className="rounded-full border border-blue-200 px-3 py-1 text-xs font-medium text-blue-700 transition hover:bg-white disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                    disabled={!fieldForm.visibleWhen}
                  >
                    Limpar condições
                  </button>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700" htmlFor="expression-field">
                      1. Qual resposta controla a visibilidade?
                    </label>
                    <select
                      id="expression-field"
                      className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={expressionBuilder.fieldId}
                      onChange={(event) => handleExpressionBuilderFieldChange(event.target.value)}
                    >
                      <option value="">Selecione um campo do formulário</option>
                      {fields.map((field) => (
                        <option key={field.id} value={field.id}>
                          {field.label || field.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700" htmlFor="expression-operator">
                      2. Qual comparação deve ser feita?
                    </label>
                    <select
                      id="expression-operator"
                      className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={expressionBuilder.operator}
                      onChange={(event) => handleExpressionBuilderChange('operator', event.target.value)}
                      disabled={!expressionBuilder.fieldId}
                    >
                      {expressionBuilderOperators.map((operator) => (
                        <option key={operator} value={operator}>
                          {EXPRESSION_OPERATOR_LABELS[operator] || operator}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700" htmlFor="expression-value">
                      3. Qual valor deve aparecer na resposta?
                    </label>
                    {expressionBuilder.valueType === 'boolean' ? (
                      <select
                        id="expression-value"
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={expressionBuilder.value}
                        onChange={(event) => handleExpressionBuilderChange('value', event.target.value)}
                        disabled={!expressionBuilder.fieldId}
                      >
                        <option value="true">Sim</option>
                        <option value="false">Não</option>
                      </select>
                    ) : expressionBuilderFieldOptions.length > 0 ? (
                      <select
                        id="expression-value"
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={expressionBuilder.value}
                        onChange={(event) => handleExpressionBuilderChange('value', event.target.value)}
                        disabled={!expressionBuilder.fieldId}
                      >
                        <option value="">Selecione uma opção preenchida</option>
                        {expressionBuilderFieldOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id="expression-value"
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        type={expressionBuilder.valueType === 'number' ? 'number' : 'text'}
                        value={expressionBuilder.value}
                        onChange={(event) => handleExpressionBuilderChange('value', event.target.value)}
                        placeholder={expressionBuilder.valueType === 'number' ? 'Digite um número' : "Ex.: aprovado"}
                        disabled={!expressionBuilder.fieldId}
                      />
                    )}
                  </div>
                  {fieldForm.visibleWhen && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-700" htmlFor="expression-joiner">
                        Como combinar com as condições já criadas?
                      </label>
                      <select
                        id="expression-joiner"
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={expressionBuilder.joiner}
                        onChange={(event) => handleExpressionBuilderChange('joiner', event.target.value)}
                      >
                        {Object.entries(EXPRESSION_JOINER_LABELS).map(([joiner, label]) => (
                          <option key={joiner} value={joiner}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={insertExpressionSnippet}
                    disabled={!canInsertExpression}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    Adicionar condição
                  </button>
                  {expressionBuilder.fieldId && expressionBuilderFieldOptions.length > 0 && expressionBuilder.valueType === 'text' && (
                    <span className="text-[11px] text-slate-500">
                      As opções cadastradas aparecem automaticamente para evitar erros de digitação.
                    </span>
                  )}
                </div>
              </div>
            </div>
            {fieldForm.type === 'list' && isAcademicField && (
              <div className="md:col-span-2 space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h4 className="text-sm font-semibold text-blue-900">Opções do cadastro</h4>
                {academicCourseField ? (
                  <p className="text-sm text-blue-900">As turmas serão filtradas automaticamente pelo campo "{academicCourseField.label}". Ao trocar o curso, uma turma incompatível será desmarcada.</p>
                ) : (
                  <p className="text-sm text-blue-900">{integratedAcademicKind(fieldForm) === 'class' ? 'Inclua o campo Curso da integração para filtrar as turmas automaticamente.' : 'Os cursos são atualizados a partir do cadastro do módulo.'}</p>
                )}
                <ul className="max-h-64 space-y-2 overflow-y-auto">
                  {(fieldForm.academicOptions || []).map(option => (
                    <li key={option.value} className="rounded border border-blue-100 bg-white px-3 py-2 text-sm text-slate-800">
                      <span>{option.label}</span>
                      {academicCourseField && <span className="mt-1 block text-xs text-slate-500">{academicCourseField.academicOptions?.find(course => String(course.value) === String(option.parentId))?.label || 'Sem curso vinculado'}</span>}
                    </li>
                  ))}
                </ul>
                {fieldForm.academicOptions.length === 0 && <p className="text-sm text-slate-600">Nenhuma opção cadastrada.</p>}
              </div>
            )}
            {fieldForm.type === 'list' && !isAcademicField && (
              <div className="md:col-span-2 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-700" htmlFor="field-options-0">
                    Opções
                  </label>
                  <button
                    type="button"
                    onClick={addListOption}
                    className="flex items-center gap-2 rounded border border-blue-200 px-2 py-1 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
                  >
                    <FaPlus className="h-3 w-3" /> Adicionar opção
                  </button>
                </div>
                <div className="space-y-2">
                  {fieldForm.options.map((option, index) => (
                    <div key={`field-option-${index}`} className="flex flex-wrap items-center gap-2">
                      <input
                        id={index === 0 ? 'field-options-0' : undefined}
                        className="flex-1 min-w-[180px] rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={option}
                        onChange={(event) => updateListOption(index, event.target.value)}
                        placeholder={`Opção ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeListOption(index)}
                        className="rounded border border-red-200 px-2 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                        disabled={fieldForm.options.length === 1}
                        aria-label={`Remover opção ${index + 1}`}
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-600">Como o campo deve se comportar?</p>
                  <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-700">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="allowMultiple"
                        value="single"
                        checked={!fieldForm.allowMultiple}
                        onChange={() => setFieldForm((prev) => ({ ...prev, allowMultiple: false }))}
                      />
                      Seleção única
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="allowMultiple"
                        value="multiple"
                        checked={fieldForm.allowMultiple}
                        onChange={() => setFieldForm((prev) => ({ ...prev, allowMultiple: true }))}
                      />
                      Seleção múltipla
                    </label>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    A primeira opção será sugerida como padrão na seleção única. Em listas múltiplas, o usuário poderá marcar
                    quantos itens forem necessários.
                  </p>
                </div>
              </div>
            )}
            {fieldForm.type === 'list' && !isAcademicField && (
              <fieldset className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
                <legend className="px-1 text-sm font-semibold text-slate-700">Resposta personalizada</legend>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={fieldForm.allowOtherOption} onChange={event => setFieldForm(prev => ({ ...prev, allowOtherOption: event.target.checked }))} />
                  Permitir “Outra opção”
                </label>
                {fieldForm.allowOtherOption && <div className="grid gap-4 md:grid-cols-2">
                  <label className="text-sm text-slate-700">Texto da opção
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={fieldForm.otherOptionLabel} placeholder="Outra opção"
                      onChange={event => setFieldForm(prev => ({ ...prev, otherOptionLabel: event.target.value }))} />
                  </label>
                  <label className="text-sm text-slate-700">Caracteres permitidos
                    <select className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={fieldForm.otherOptionInput}
                      onChange={event => setFieldForm(prev => ({ ...prev, otherOptionInput: event.target.value }))}>
                      <option value="all">Tudo (letras, números e símbolos)</option><option value="letters">Somente letras</option>
                      <option value="numbers">Somente números</option><option value="alphanumeric">Letras e números</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={fieldForm.otherOptionUppercase}
                    onChange={event => setFieldForm(prev => ({ ...prev, otherOptionUppercase: event.target.checked }))} />Converter para MAIÚSCULAS</label>
                  <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={fieldForm.otherOptionAllowAccents}
                    onChange={event => setFieldForm(prev => ({ ...prev, otherOptionAllowAccents: event.target.checked }))} />Permitir acentos</label>
                  <p className="md:col-span-2 text-xs text-slate-500">Ao selecionar esta opção, a pessoa poderá digitar uma resposta. O texto padrão é “Outra opção”.</p>
                </div>}
              </fieldset>
            )}
            {fieldForm.type === 'decimal' && (
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="field-decimal">Casas decimais</label>
                <IntegerInput
                  id="field-decimal"
                  name="decimalPlaces"
                  min="0"
                  max="10"
                  value={fieldForm.decimalPlaces}
                  onChange={handleFieldChange}
                  placeholder="2"
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </div>
            )}
            {fieldForm.type === 'linkedField' && <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700">Origem dos dados
                <select aria-label="Origem dos dados" className="mt-1 w-full rounded border border-slate-300 px-3 py-2" value={fieldForm.linkedSourceType || 'process'} onChange={event => setFieldForm(prev => ({ ...prev, linkedSourceType: event.target.value, linkedModuleKey: '', linkedProcessId: '', linkedFormId: '', linkedFieldId: '', moduleMappings: [], multiple: false }))}>
                  <option value="process">Respostas de outro processo</option>
                  <option value="module">Dados de um módulo</option>
                </select>
              </label>
            </div>}
            {fieldForm.type === 'linkedField' && fieldForm.linkedSourceType === 'module' && <ModuleRecordSettings field={fieldForm} fields={fields} onChange={patch => setFieldForm(prev => ({ ...prev, ...patch }))} />}
            {fieldForm.type === 'linkedField' && fieldForm.linkedSourceType !== 'module' && (
              <div className="md:col-span-2 grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Processo de origem</label>
                  <select
                    name="linkedProcessId"
                    value={fieldForm.linkedProcessId || ''}
                    onChange={(event) =>
                      setFieldForm((prev) => ({
                        ...prev,
                        linkedProcessId: event.target.value,
                        linkedFormId: '',
                        linkedFieldId: '',
                        linkedFieldMappings: [],
                      }))
                    }
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                  >
                    <option value="">Selecione um processo</option>
                    {linkedProcessOptions.map((process) => (
                      <option key={process.id} value={process.id}>
                        {process.name}
                      </option>
                    ))}
                  </select>
                  {loadingLinkedFieldSources && (
                    <p className="mt-1 text-xs text-slate-500">Carregando processos disponíveis...</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Campo de origem</label>
                  <select
                    name="linkedFieldId"
                    value={fieldForm.linkedFieldId || ''}
                    onChange={handleFieldChange}
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                    disabled={!selectedLinkedForm}
                  >
                    <option value="">
                      {selectedLinkedForm ? 'Selecione um campo' : 'Selecione um processo primeiro'}
                    </option>
                    {(selectedLinkedForm?.fields || []).map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.label || field.id}
                      </option>
                    ))}
                  </select>
                  {selectedLinkedForm && (
                    <p className="mt-1 text-xs text-slate-500">Formulário de origem: {selectedLinkedForm.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Status de origem</label>
                  <select name="sourceStatus" value={fieldForm.sourceStatus || 'completed'} onChange={handleFieldChange} className="mt-1 w-full rounded border border-slate-300 px-3 py-2">
                    <option value="completed">Somente concluídas</option>
                    <option value="active_and_completed">Concluídas e em andamento</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <input type="checkbox" name="multiple" checked={Boolean(fieldForm.multiple)} onChange={handleFieldChange} />
                  Permitir múltiplas seleções
                </label>
                <div className="md:col-span-2 space-y-3 rounded border border-blue-100 bg-blue-50/60 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Puxar outros campos do mesmo cadastro</p>
                      <p className="text-xs text-slate-600">Opcional: ao escolher o registro acima, copie outros dados do mesmo processo para campos deste formulário.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFieldForm((prev) => ({
                        ...prev,
                        linkedFieldMappings: [...(prev.linkedFieldMappings || []), { sourceFieldId: '', targetFieldId: '' }],
                      }))}
                      disabled={!selectedLinkedForm || Boolean(fieldForm.multiple)}
                      className="inline-flex items-center gap-1 rounded border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FaPlus className="text-[10px]" /> Adicionar campo
                    </button>
                  </div>
                  {fieldForm.multiple && (
                    <p className="text-xs text-amber-700">O preenchimento de campos adicionais funciona com seleção única.</p>
                  )}
                  {(fieldForm.linkedFieldMappings || []).map((mapping, mappingIndex) => (
                    <div key={`linked-map-${mappingIndex}`} className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                      <select
                        value={mapping.sourceFieldId || ''}
                        onChange={(event) => setFieldForm((prev) => ({
                          ...prev,
                          linkedFieldMappings: (prev.linkedFieldMappings || []).map((item, index) =>
                            index === mappingIndex ? { ...item, sourceFieldId: event.target.value } : item
                          ),
                        }))}
                        className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Campo do processo de origem</option>
                        {(selectedLinkedForm?.fields || []).map((sourceField) => (
                          <option key={`source-${mappingIndex}-${sourceField.id}`} value={sourceField.id}>
                            {sourceField.label || sourceField.id}
                          </option>
                        ))}
                      </select>
                      <select
                        value={mapping.targetFieldId || ''}
                        onChange={(event) => setFieldForm((prev) => ({
                          ...prev,
                          linkedFieldMappings: (prev.linkedFieldMappings || []).map((item, index) =>
                            index === mappingIndex ? { ...item, targetFieldId: event.target.value } : item
                          ),
                        }))}
                        className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Preencher campo deste formulário</option>
                        {fields.filter((targetField) => targetField.id && targetField.id !== fieldForm.id).map((targetField) => (
                          <option key={`target-${mappingIndex}-${targetField.id}`} value={targetField.id}>
                            {targetField.label || targetField.id}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setFieldForm((prev) => ({
                          ...prev,
                          linkedFieldMappings: (prev.linkedFieldMappings || []).filter((_, index) => index !== mappingIndex),
                        }))}
                        className="rounded border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {['document', 'signature-attachment'].includes(fieldForm.type) && <SignatureSettings field={fieldForm} fields={fields} onChange={signature => setFieldForm(prev => ({ ...prev, signature }))} />}
            {fieldForm.type === 'document' && (
              <div className="md:col-span-2 space-y-3 rounded border border-indigo-100 bg-indigo-50/70 p-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700" htmlFor="document-title">Título do documento</label>
                  <input
                    id="document-title"
                    value={fieldForm.documentTemplate?.title || ''}
                    onChange={(event) => setFieldForm((prev) => ({
                      ...prev,
                      documentTemplate: { ...(prev.documentTemplate || {}), title: event.target.value },
                    }))}
                    placeholder="Ex.: Contrato de prestação de serviços"
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 rounded bg-white p-2 text-xs font-semibold text-indigo-700">
                  <button type="button" onClick={() => applyDocumentCommand('bold')} className="inline-flex items-center gap-1 rounded border border-indigo-100 px-2 py-1 hover:bg-indigo-50"><FaBold /> Negrito</button>
                  <button type="button" onClick={() => applyDocumentCommand('underline')} className="inline-flex items-center gap-1 rounded border border-indigo-100 px-2 py-1 hover:bg-indigo-50"><FaUnderline /> Sublinhado</button>
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-indigo-100 px-2 py-1 hover:bg-indigo-50">
                    <FaPalette /> Cor
                    <input type="color" onChange={handleDocumentColor} className="h-5 w-7 cursor-pointer border-0 bg-transparent p-0" aria-label="Escolher cor do texto" />
                  </label>
                  <label className="inline-flex items-center gap-1 rounded border border-indigo-100 px-2 py-1 hover:bg-indigo-50">
                    <FaFont /> Fonte
                    <select defaultValue="" onChange={handleDocumentFontFamily} className="bg-transparent text-xs outline-none">
                      <option value="" disabled>Selecione</option>
                      <option value="Arial">Arial</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Courier New">Courier New</option>
                      <option value="Verdana">Verdana</option>
                      <option value="Georgia">Georgia</option>
                    </select>
                  </label>
                  <label className="inline-flex items-center gap-1 rounded border border-indigo-100 px-2 py-1 hover:bg-indigo-50">
                    <FaFont /> Tamanho
                    <select defaultValue="" onChange={handleDocumentFontSize} className="bg-transparent text-xs outline-none">
                      <option value="" disabled>Selecione</option>
                      <option value="2">Pequeno</option>
                      <option value="3">Normal</option>
                      <option value="4">Médio</option>
                      <option value="5">Grande</option>
                      <option value="6">Título</option>
                    </select>
                  </label>
                  <button type="button" onClick={cycleDocumentAlignment} className="inline-flex items-center gap-1 rounded border border-indigo-100 px-2 py-1 hover:bg-indigo-50"><SelectedDocumentAlignmentIcon /> {selectedDocumentAlignment.label}</button>
                  <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={applyDocumentMarker} className="inline-flex items-center gap-1 rounded border border-indigo-100 px-2 py-1 hover:bg-indigo-50"><FaListUl /> Marcador</button>
                  <button type="button" onClick={() => setDocumentEditorExpanded((prev) => !prev)} className="inline-flex items-center gap-1 rounded border border-indigo-100 px-2 py-1 hover:bg-indigo-50">
                    {documentEditorExpanded ? <FaCompress /> : <FaExpand />} {documentEditorExpanded ? 'Reduzir tela' : 'Expandir tela'}
                  </button>
                  <button type="button" onClick={handleDocumentPreview} className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700 hover:bg-emerald-100"><FaEye /> Ver prévia</button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700" htmlFor="document-content">Texto do documento</label>
                  <div
                    id="document-content"
                    ref={documentEditorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={syncDocumentEditorContent}
                    onBlur={syncDocumentEditorContent}
                    onPaste={handleDocumentPaste}
                    data-placeholder="Escreva o texto. Use variáveis como {{nome}} para puxar campos preenchidos."
                    style={{ textAlign: documentAlignment, listStylePosition: 'inside' }}
                    className={`mt-1 w-full overflow-y-auto rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)] ${documentEditorExpanded ? 'min-h-[48vh] max-h-[48vh]' : 'min-h-[180px] max-h-[320px]'}`}
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    O PDF gerado terá logo e dados cadastrais da empresa, título, conteúdo, paginação no canto direito e o rodapé “Documento gerado pelo sistema Intelligent BPM Nash.” centralizado.
                  </p>
                </div>
                {availableTemplateFields.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Inserir campo preenchido</p>
                    <div className="flex flex-wrap gap-2">
                      {availableTemplateFields.map((field) => (
                        <button
                          key={`document-token-${field.id}`}
                          type="button"
                          onClick={() => insertDocumentHtml(`<span data-field-placeholder="${field.id}" style="color:#2563eb;font-weight:600">{{${field.id}}}</span>&nbsp;`)}
                          className="rounded border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                        >
                          {field.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {fieldForm.type === 'file' && (
              <div className="md:col-span-2 space-y-3 rounded border border-blue-100 bg-blue-50/60 p-3">
                {isPersonAttachmentIntegration && (
                  <IntegratedFileCategorySelect {...attachmentCategories} value={fieldForm.fileCategory}
                    onChange={value => setFieldForm(prev => ({ ...prev, fileCategory: value }))} />
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700" htmlFor="field-file-template">
                    Nome automático do arquivo
                  </label>
                  <textarea
                    id="field-file-template"
                    ref={fileNameTemplateRef}
                    name="fileNameTemplate"
                    value={fieldForm.fileNameTemplate}
                    onChange={handleFieldChange}
                    rows={Math.max(2, fieldForm.fileNameTemplate.split('\n').length)}
                    placeholder="Ex.: Atestado de {{nome}} de {{data_atestado}}"
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    Combine textos livres com valores de outros campos usando{' '}
                    <code className="rounded bg-white px-1">{'{{idDoCampo}}'}</code>. A extensão original é mantida e
                    caracteres inválidos são ajustados automaticamente.
                  </p>
                </div>
                {(specialTemplateTokens.length > 0 || availableTemplateFields.length > 0) && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Inserir variável
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {specialTemplateTokens.map((token) => (
                        <button
                          key={`special-token-${token.id}`}
                          type="button"
                          onClick={() => insertFileNamePlaceholder(token.id)}
                          className="rounded border border-blue-200 bg-white px-2 py-1 text-xs font-semibold text-blue-600 transition hover:bg-blue-100"
                        >
                          {token.label}
                        </button>
                      ))}
                      {availableTemplateFields.map((field) => (
                        <button
                          key={`field-token-${field.id}`}
                          type="button"
                          onClick={() => insertFileNamePlaceholder(field.id)}
                          className="flex items-center gap-2 rounded border border-blue-200 bg-white px-2 py-1 text-xs font-semibold text-blue-600 transition hover:bg-blue-100"
                        >
                          <span>{field.label}</span>
                          <span className="rounded bg-blue-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-blue-700">
                            {getFieldTypeLabel(field.type)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {fileNamePreview && (
                  <p className="text-xs text-slate-600">
                    Exemplo com campos preenchidos:
                    <span className="ml-1 font-mono text-slate-800">{fileNamePreview}</span>
                  </p>
                )}
              </div>
            )}

            {fieldForm.type !== 'table' && (
              <div className="md:col-span-2 space-y-2 rounded border border-slate-200 bg-slate-50 p-3">
                <label className="block text-sm font-medium text-slate-700" htmlFor="field-auto-fill-source">
                  Preenchimento automático
                </label>
                <select
                  id="field-auto-fill-source"
                  name="autoFillSource"
                  value={fieldForm.autoFillSource}
                  onChange={handleFieldChange}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {AUTO_FILL_OPTIONS.map((option) => (
                    <option key={option.value || 'none'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {fieldForm.autoFillSource && (
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      name="autoFillEditable"
                      checked={fieldForm.autoFillEditable}
                      onChange={handleFieldChange}
                    />
                    Permitir que o usuário altere o valor preenchido automaticamente
                  </label>
                )}
              </div>
            )}
            {fieldForm.type === 'table' && (
              <div className="md:col-span-2 space-y-3 rounded border border-blue-100 bg-blue-50/50 p-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-700">Colunas da tabela</label>
                  <button
                    type="button"
                    onClick={() =>
                      setFieldForm((prev) => ({
                        ...prev,
                        tableColumns: [
                          ...(prev.tableColumns || []),
                          { id: `coluna_${(prev.tableColumns || []).length + 1}`, label: '', type: 'text', required: false },
                        ],
                      }))
                    }
                    className="rounded border border-blue-200 px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-100"
                  >
                    + Coluna
                  </button>
                </div>
                {(fieldForm.tableColumns || []).length === 0 && (
                  <p className="text-xs text-slate-600">Adicione ao menos uma coluna para montar a tabela.</p>
                )}
                {(fieldForm.tableColumns || []).map((column, index) => (
                  <div key={`column-${index}`} className="grid gap-2 rounded border border-slate-200 bg-white p-2 md:grid-cols-12">
                    <input
                      value={column.label}
                      onChange={(event) =>
                        setFieldForm((prev) => ({
                          ...prev,
                          tableColumns: (prev.tableColumns || []).map((item, itemIndex) =>
                            itemIndex === index
                              ? {
                                  ...item,
                                  label: event.target.value,
                                  id: event.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || `coluna_${index + 1}`,
                                }
                              : item
                          ),
                        }))
                      }
                      placeholder="Nome da coluna"
                      className="md:col-span-5 rounded border border-slate-300 px-2 py-1 text-sm"
                    />
                    <select
                      value={column.type || 'text'}
                      onChange={(event) =>
                        setFieldForm((prev) => ({
                          ...prev,
                          tableColumns: (prev.tableColumns || []).map((item, itemIndex) =>
                            itemIndex === index ? { ...item, type: event.target.value } : item
                          ),
                        }))
                      }
                      className="md:col-span-4 rounded border border-slate-300 px-2 py-1 text-sm"
                    >
                      {TABLE_COLUMN_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <label className="md:col-span-2 flex items-center gap-1 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={Boolean(column.required)}
                        onChange={(event) =>
                          setFieldForm((prev) => ({
                            ...prev,
                            tableColumns: (prev.tableColumns || []).map((item, itemIndex) =>
                              itemIndex === index ? { ...item, required: event.target.checked } : item
                            ),
                          }))
                        }
                      />
                      Obrigatória
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setFieldForm((prev) => ({
                          ...prev,
                          tableColumns: (prev.tableColumns || []).filter((_, itemIndex) => itemIndex !== index),
                        }))
                      }
                      className="md:col-span-1 rounded border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                    >
                      X
                    </button>
                    {column.type === 'file' && isPersonAttachmentIntegration && (
                      <div className="md:col-span-12 min-w-0">
                        <IntegratedFileCategorySelect {...attachmentCategories} label={`Categoria do arquivo — ${column.label || `Coluna ${index + 1}`}`}
                          value={column.fileCategory || ''} onChange={value => setFieldForm(prev => ({ ...prev,
                            tableColumns: (prev.tableColumns || []).map((item, itemIndex) => itemIndex === index ? { ...item, fileCategory: value } : item),
                          }))} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {fieldForm.type === 'dynamic-select' && (
              <div className="md:col-span-2 space-y-2 rounded border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-600">
                  Na etapa inicial, o responsável poderá cadastrar as opções e definir se o próximo usuário poderá escolher
                  apenas uma ou várias alternativas.
                </p>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    name="allowMultiple"
                    checked={fieldForm.allowMultiple}
                    onChange={handleFieldChange}
                  />
                  Permitir múltiplas seleções por padrão
                </label>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              onClick={saveField}
              disabled={isFieldSizeInvalid}
            >
              Salvar
            </button>
            {editingIndex !== null && (
              <button
                type="button"
                onClick={deleteField}
                className="rounded border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
              >
                Excluir
              </button>
            )}
            <button
              type="button"
              onClick={closeFieldModal}
              className="rounded border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600"
            >
              Cancelar
            </button>
          </div>
        </div>
      </Modal>
      <Modal open={groupModalOpen} onClose={closeGroupModal} anchor={groupModalAnchor}>
        <h2 className="text-xl mb-2">{groupEditingIndex !== null ? 'Editar Grupo' : 'Novo Grupo'}</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="group-label">Nome exibido</label>
            <input
              id="group-label"
              name="label"
              placeholder="Ex.: Informações gerais"
              value={groupForm.label}
              onChange={handleGroupChange}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="group-id">ID (sem espaços)</label>
            <input
              id="group-id"
              name="id"
              placeholder="Ex.: informacoes"
              value={groupForm.id}
              onChange={handleGroupChange}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={groupEditingIndex !== null}
            />
            <p className="mt-1 text-xs text-slate-500">
              O ID é usado para referenciar o grupo nos processos. Ele não pode ser alterado após criado.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700" onClick={saveGroup}>
              Salvar
            </button>
            {groupEditingIndex !== null && (
              <button type="button" onClick={deleteGroup} className="rounded border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                Excluir
              </button>
            )}
            <button type="button" onClick={closeGroupModal} className="rounded border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600">
              Cancelar
            </button>
          </div>
        </div>
      </Modal>
      <Modal open={moduleIntegrationModalOpen} onClose={closeModuleIntegrationModal} anchor={moduleIntegrationModalAnchor}>
        {pendingIntegrationTemplate ? <ModuleIntegrationUpdateChoice template={pendingIntegrationTemplate} onChoose={(mode) => commitModuleIntegration(pendingIntegrationTemplate, mode)} onCancel={closeModuleIntegrationModal} /> : <div className="space-y-4">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
              <FaPlug className="text-indigo-600" /> Integração de módulos.
            </h2>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {moduleIntegrationTemplates.map((template) => (
              <div key={template.id} className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 shadow-sm">
                <div className="flex h-full flex-col justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">{template.targetModule}</p>
                    <h3 className="mt-1 text-lg font-semibold text-slate-900">{template.action}</h3>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                      <span className="rounded-full bg-white px-2 py-1">{template.groups.length} grupos</span>
                      <span className="rounded-full bg-white px-2 py-1">{template.fields.length} campos</span>
                      <span className="rounded-full bg-white px-2 py-1">{template.fields.filter((field) => field.required).length} obrigatórios</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => applyModuleIntegrationTemplate(template)}
                    className="inline-flex items-center justify-center gap-2 rounded bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    <FaPlug /> Usar integração
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>}
      </Modal>
      <Modal open={templateModalOpen} onClose={closeTemplateModal} anchor={templateModalAnchor}>
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Modelos prontos</h2>
            <p className="mt-1 text-sm text-slate-600">
              Escolha um modelo para começar mais rápido. Você pode editar tudo após aplicá-lo.
            </p>
          </div>
          <div className="space-y-3">
            {allTemplates.map((template) => (
              <div
                key={template.id}
                className="rounded-lg border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{template.name}</h3>
                    <p className="mt-1 text-sm text-slate-600">{template.description}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                      {template.groups.length > 0 && (
                        <span className="rounded bg-white px-2 py-0.5">{template.groups.length} grupos</span>
                      )}
                      <span className="rounded bg-white px-2 py-0.5">{template.fields.length} campos</span>
                      <span className="rounded bg-white px-2 py-0.5">
                        Tipos: {Array.from(new Set(template.fields.map((f) => getFieldTypeLabel(f.type)))).join(', ')}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => applyTemplate(template)}
                    className="self-start rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                  >
                    Usar este modelo
                  </button>
                </div>
                {template.isCustom && (
                  <button
                    type="button"
                    onClick={() => deleteCustomTemplate(template.id)}
                    className="mt-3 inline-flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:bg-red-100"
                  >
                    Remover modelo
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
            <h3 className="text-lg font-semibold text-blue-900">Criar novo modelo</h3>
            <p className="mt-1 text-sm text-blue-800/80">
              Salve a configuração atual do formulário para reutilizar quando desejar.
            </p>
            {!isCreatingTemplate ? (
              <button
                type="button"
                onClick={startTemplateCreation}
                className="mt-3 inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <FaMagic /> Salvar como modelo
              </button>
            ) : (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-blue-900" htmlFor="template-name">Nome do modelo</label>
                  <input
                    id="template-name"
                    name="name"
                    value={templateForm.name}
                    onChange={handleTemplateFormChange}
                    placeholder="Ex.: Formulário de fornecedores"
                    className="mt-1 w-full rounded border border-blue-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-900" htmlFor="template-description">Descrição (opcional)</label>
                  <textarea
                    id="template-description"
                    name="description"
                    rows={3}
                    value={templateForm.description}
                    onChange={handleTemplateFormChange}
                    className="mt-1 w-full rounded border border-blue-200 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="Descreva quando utilizar este modelo"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={saveCurrentAsTemplate}
                    className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                  >
                    Salvar modelo
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreatingTemplate(false)}
                    className="inline-flex items-center gap-2 rounded border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
      <Toast message={toastMsg} onClose={() => setToastMsg('')} />
    </div>
  );
}
